import { openOwnSend, sendingSettings } from '../email-send'
import { dayVisitCounts } from '../day-visits'
import { bottomSheet } from '../due-bar'
import { directionsHref, placeInMapsLink } from '../../functions/lib/maps-link'
import { mountTodayDoneHint } from '../today-done-hint'
import { choicesOf, guessKind, stopLines, stopMark, type ChosenField, type StopNotes } from '../note-kind'
import { askedBadges, extraBadges } from '../extras'
import { lateWay } from '../contact'
import { missedWorkerMessage, onAPhone, whatsappHref } from '../rebook-message'
import { askAboutDay, keepDayDone, showDayDone, type Placement } from './rebook'
import { askParking } from '../parking-ask'
import { dayLoad, nowWhose, visitsWords } from '../booking-days'
import { forgetToTell, keepToTell, moveDays, toTellList, type ToTell } from '../missed-visit'
import { offerUndo } from '../undo'
import { api, dismiss, getQueue, isLocalOnly, mutate, onQueueChange, postNow, readCache, saveCache, type Cached } from '../api'
import { saveDayCopy } from '../day-copies'
import { CLEAN_PATH, describeChange, layWaiting } from '../waiting-changes'
import { cardTitle, changeLine, diffWindows, isChangedCard, isSeenWindow, ownFields, seenOf, type ChangedCard, type SeenWindow } from '../jobs-changed'
import { deviceId } from '../offline-identity'
import { kindLabel } from '../trade'
import { severalJobsOn } from '../job-names'
import { dayStops, houseOrder, windowsDayBefore } from '../jobs'
import { openBooking } from '../booking'
import { undoPatch } from '../undo'
import {
  cleanPhone,
  firstName,
  lateMessage,
  lateNoteLine,
  noteClock,
  smsHref,
} from '../late-message'
import { businessSettings, parkingNotesOn, setTimesOn, visibleTime } from '../business-settings'
import { getMe, ownerDoesJobs, pricesHidden, worksAlone } from '../me'
import { visitBadge, paymentWordsBadge } from '../visit-badge'
import { visitWords } from '../visit-words'
import { diaryCustomer } from '../visit-words'
import { isContact, isSuggestion } from '../planning'
import { alsoOwesWords, creditWords, fromCredit, moneyPending, toCollect, visitReceived } from '../brought'
import { attachPhotos, hasPendingPhotoDrafts } from '../photo-note'
import { routeGen } from '../route-state'
import { todayTallyLine, workWords } from '../today-tally'
import { finishDayCard, finishedDaysArea, openFinishDay, pencilledIn } from '../day-close'
import type { Clean, JobTimeHow, TodayClean, TodayPayload } from '../types'
import { attachVoiceNote } from '../voice-note'
import { bandOf, orderDay, strictMinutes } from '../day-order'
import { clockOf, jobTimesLine, nowStamps } from '../job-times'
import { shownClock } from '../round-config'
import { openTimePicker } from '../visit-time'
import { noteNode } from '../note-lines'
import { accessNoteItems, addNoteLink, noteLine, noteRow } from '../today-notes'
import { hasInsides, partBadge, priceBadges } from '../inside-price'
import { openVisitPrice, priceButton } from '../price-edit'
import { lineDrawing } from '../picture-tiles'
import type { PinStop } from '../day-map'
import '../today-b.css'
import { remindersRow } from '../reminders'
import { noticedAt, nudgesRow, readNoticed } from '../timer-nudges'
import { withoutPrices } from '../hidden-prices'
import { changeTimesChooser, guessFinish, guessStart, timeChooser, type ChangeTimesSave, type TimeGuess } from '../job-time-ask'
import { cardRowNow, cardTapsTaken } from '../lock-card'
import { contactButton, contactLines } from '../contact-tile'
import { slideToConfirm } from '../slide-confirm'
import { foldedAccessNotes, lastTimeBox, workerNoteBox, workerNotesBar } from '../worker-notes'
import { jobCountCard, jobCountFacts, type JobCountFacts } from '../job-count'
import {
  addDaysISO,
  blockedChoiceOf,
  blockedClock,
  blockedNoteText,
  blockedSaveOf,
  blockedWords,
  BLOCKED_CHOICES,
  OUT_OF_TIME_ROOM,
  clear,
  clockLabel,
  dayLabel,
  el,
  loadError,
  money,
  shownVisitTime,
  splitAccessWarning,
  telHref,
  toast,
  todayISO,
} from '../ui'

// Today — the WORKER's whole app: one 15-day strip (7 back read-only · today
// · 7 ahead) and only that worker's visits. Everything here is server-scoped
// (see functions/api/today.ts); this screen is the usable half, not the
// boundary. The Done button opens the SAME log-completed panel the owner's
// diary uses (openBooking), reduced to the fields a worker may change.
//
// Pattern copied from rebook.ts: a request sequence so a slow answer can
// never land after a newer tap, routeGen so a route change mid-load cannot
// render, and reads cached per day (`today:<date>`) so a dead zone still
// shows the day this phone last saw, behind the "as it was at HH:MM" banner.

const STRIP_BACK = 7
const STRIP_AHEAD = 7

// An owner doing their own jobs: the name their Today asks for (see load).
let ownName: string | null | undefined

// An owner's Today (Liam, 2026-09-25): "Everyone" shows who is out and their
// jobs as their phones show them; an owner who does jobs can switch to "Just
// mine". The choice is kept on this phone. A worker's Today is always theirs.
type OwnerView = 'everyone' | 'mine'
const VIEW_KEY = 'roundly:today-view'
function readView(): OwnerView {
  try {
    return localStorage.getItem(VIEW_KEY) === 'mine' ? 'mine' : 'everyone'
  } catch {
    return 'everyone'
  }
}
let ownerView: OwnerView = readView()
function isOwnerToday(): boolean {
  return getMe()?.role === 'owner'
}
// A one-person round (0078; Liam, 19 Oct 2026: "the point of this was to drop unnecessary UI"): always the owner's own
// day, with no Everyone / Just mine. The choice saved on this phone is ignored and never written, so a team round signed
// in on the same phone still opens the way it was left.
function showingMine(): boolean {
  return !isOwnerToday() || ((worksAlone() || ownerView === 'mine') && !!ownName)
}
let selectedDate = todayISO()
let firstMount = true
let requestSequence = 0
// "What's next" → Open Today (29 Sep): today, the whole day (not a saved "Just mine" that could hide the job),
// and that job glowing once the day is drawn from a fresh read.
let focusId: number | null = null
// Set by focusToday for the one mount it causes; any other visit to Today clears an old request (Codex).
let focusFresh = false
// The owner's own "Everyone / Just mine" choice, put back on their next ordinary visit (Codex).
let restoreView: OwnerView | null = null
export function focusToday(cleanId: number): void {
  focusId = cleanId
  selectedDate = todayISO()
  if (isOwnerToday() && ownerView !== 'everyone') { restoreView ??= ownerView; ownerView = 'everyone' }
  if (window.location.hash.startsWith('#/today')) void load()
  else { focusFresh = true; window.location.hash = '#/today' }
}
// Today opens at the next job to do (Liam, 13 Oct 2026: "today should open at the next one to do ie if marked as
// confirmed done it would start it after this saves scrolling down to the job"): on the first draw of a visit to
// today's date, the first job still to do (a started one first, as it comes first in the day) comes up under the
// header, unless the person has already scrolled or the screen was opened on a particular job. Once per visit to
// Today, never on the refreshes after it, so the page never jumps while someone is reading it.
let openAtNextDue = false
function openAtNext(): void {
  if (!openAtNextDue) return
  openAtNextDue = false
  if (selectedDate !== todayISO() || window.scrollY > 40) return
  const first = document.querySelector<HTMLElement>('#today-day .today-clean')
  const job = document.querySelector<HTMLElement>('#today-day [data-to-do]')
  const card = job?.closest<HTMLElement>('.today-house') ?? job
  if (!card || card === first || card.contains(first)) return
  const bar = document.querySelector<HTMLElement>('header.topbar, header, nav')?.getBoundingClientRect().bottom ?? 0
  window.scrollTo({ top: Math.max(0, window.scrollY + card.getBoundingClientRect().top - Math.max(0, bar) - 16), behavior: 'auto' })
}

function showFocus(): void {
  if (focusId === null) return
  const card = document.querySelector<HTMLElement>(`#today-day .today-clean[data-clean-id="${focusId}"]`)
  focusId = null
  if (!card) return
  // Its person's group and its stop open first, so the glow is seen (Astra).
  for (let g = card.closest<HTMLDetailsElement>('details'); g; g = g.parentElement?.closest<HTMLDetailsElement>('details') ?? null) g.open = true
  const stop = card.closest<HTMLElement>('.today-stops > .today-stop')
  if (stop) openStop(stop)
  card.classList.add('first-visit-glow')
  const bar = document.querySelector<HTMLElement>('header.topbar, header, nav')?.getBoundingClientRect().bottom ?? 0
  window.scrollTo({ top: Math.max(0, window.scrollY + card.getBoundingClientRect().top - Math.max(0, bar) - 16), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' })
}

export function mountToday(): void {
  if(!onAPhone()&&getMe()?.role!=='worker')void sendingSettings().catch(()=>{})
  if (!focusFresh) {
    focusId = null
    if (restoreView) { ownerView = restoreView; restoreView = null }
  }
  focusFresh = false
  takeCardAsk()
  // Opening Today replays nothing from an earlier visit: no ring settling, no tick, for work done while away (Astra).
  ringBefore.clear(); lastStates.clear(); lastBlocked.clear(); lastPending = new Map()
  // A new visit to Today opens at the next job to do (openAtNext).
  openAtNextDue = true
  bindResume()
  // The app lives on a phone for days at a time: a strip left on last week
  // snaps forward, the same contract the diary's resetStaleWindow keeps.
  const today = todayISO()
  // The first time: the day was worked out when this file loaded, before start-up knew the
  // round's clock (round-config.ts, Step 1), so take it again now.
  if (firstMount) { firstMount = false; selectedDate = today; drawnToday = today }
  if (selectedDate < addDaysISO(today, -STRIP_BACK) || selectedDate > addDaysISO(today, STRIP_AHEAD)) {
    selectedDate = today
  }
  void load()
}

export function todayOnDataChanged(): void {
  // A stop's sheet open (a voice note, a reason half chosen): the day is read again when it closes.
  if (document.querySelector('#today-day dialog.stop-sheet[open], dialog.time-sheet[open]')) { reloadWaiting = true; return }
  void load()
}

// Opened from the lock screen card's Record payment, Running late or Couldn't do it (the iPhone app opens
// #/today?job=<id>&do=pay|late|couldnt; docs/plans/lock-screen/REAL-CARD.md rule 5): today, that job, and what the
// button asked for once the day is drawn. A timer nudge's "Another time…" asks do=started|finished (0083): the job's
// "When did you start/finish cleaning?" with its times to pick from. The question is taken off the address at once, so a reload does not ask
// again. A job not on the day just opens Today.
type CardAskWhat = 'pay' | 'late' | 'couldnt' | 'started' | 'finished'
const CARD_ASKS: readonly string[] = ['pay', 'late', 'couldnt', 'started', 'finished']
let cardAsk: { id: number; what: CardAskWhat | null } | null = null
function takeCardAsk(): void {
  const [path, query] = window.location.hash.split('?')
  if (path !== '#/today' || !query) return
  const q = new URLSearchParams(query)
  const id = Number(q.get('job'))
  const what = q.get('do')
  history.replaceState(history.state, '', '#/today')
  if (!Number.isSafeInteger(id) || id <= 0) return
  cardAsk = { id, what: what && CARD_ASKS.includes(what) ? (what as CardAskWhat) : null }
  focusId = id
  focusFresh = true
  selectedDate = todayISO()
  // The card shows the person's own jobs: Just mine, where the owner's unnamed jobs keep their buttons (GPT-6.1's review).
  if (isOwnerToday() && ownerView !== 'mine' && ownName) { restoreView ??= ownerView; ownerView = 'mine' }
}
// Asked once, after the card's own taps have been taken into the queue (Sonnet's review: a Record payment must see the
// card's Finish first), on the day as drawn with them laid over.
async function runCardAsk(data: TodayPayload): Promise<void> {
  const ask = cardAsk
  cardAsk = null
  if (!ask?.what) return
  if (!(await cardTapsTaken())) return
  if (selectedDate !== todayISO()) return
  // The job as known now (the last read with every saved answer since, from lock-card.ts), with what still waits in
  // the queue laid over: a Finish the intake just sent, or an Undo of it, counts (Astra's final review, round 2).
  // The newer of the two by version; a job a saved answer took off the day is not acted on (Astra's final review, round 3).
  const fromCard = cardRowNow(ask.id)
  if (fromCard === 'gone') return
  const fromToday = data.cleans.find((c) => c.id === ask.id)
  const known = fromCard && (!fromToday || fromCard.version >= fromToday.version) ? fromCard : fromToday
  const cl = known ? layWaiting([known], getQueue())[0] : undefined
  const card = document.querySelector<HTMLElement>(`#today-day .today-clean[data-clean-id="${ask.id}"]`)
  if (!cl || !card) return
  if (ask.what === 'pay') {
    // Only a job finished and not done yet: anything else just opens at the job (both reviews).
    if (cl.state !== 'booked' || !cl.finished_at) return
    openBooking({ customerId: cl.customer_id, cleanId: cl.id, completed: true,
      snapshot: { customer: { name: cl.customer_name, address: cl.address, postcode: cl.postcode, inside_price_pence: cl.customer_inside_price_pence ?? null }, clean: cl } })
  } else if (ask.what === 'late') {
    if (!card.querySelector('.late-chooser')) card.querySelector<HTMLButtonElement>('.late-btn')?.click()
  } else if (ask.what === 'started' || ask.what === 'finished') {
    // The card's own "Started a while ago?" or "Finished a while ago?", only while that is still the question: not started yet,
    // or under way. Anything else just opens at the job.
    if (card.querySelector('.time-chooser:not(.time-chooser-asked)')) return
    // The leaving question already showing in the slide's place: that is the question.
    if (ask.what === 'finished' && card.querySelector('.time-chooser-asked')) return
    card.querySelector<HTMLButtonElement>(ask.what === 'started' ? '.job-earlier-link:not(.finish-earlier-link)' : '.finish-earlier-link')?.click()
  } else if (!card.querySelector('.blocked-chooser')) {
    // Not started: its own button; under way: the one in More.
    card.querySelector<HTMLButtonElement>('.blocked-btn, .live-more-danger')?.click()
  }
}

// Which list this screen asks for: a worker's own; an owner's "Just mine"
// (their name); or an owner's "Everyone" (the whole day, by person).
function whoParam(): string {
  if (!isOwnerToday()) return ''
  if (showingMine()) return `&worker=${encodeURIComponent(ownName ?? '')}`
  return '&everyone=1'
}
function todayPath(): string {
  return `/api/today?from=${selectedDate}&to=${selectedDate}${whoParam()}`
}
function cacheKeyFor(date: string): string {
  if (!isOwnerToday()) return `today:${date}`
  return showingMine() ? `today:${ownName}:${date}` : `today:everyone:${date}`
}
function todayCacheKey(): string {
  return cacheKeyFor(selectedDate)
}
function ownFromEveryone(saved: Cached<TodayPayload> | null): Cached<TodayPayload> | null {
  if (!saved) return null
  // A visit in "cal" is Cal's here, as the switch to one person counted it (Astra's review, 20 Oct 2026).
  const own = ownName?.toLowerCase()
  const cleans = saved.data.cleans.filter((c) => !c.phone_of?.trim() || c.phone_of.trim().toLowerCase() === own)
  return { ...saved, data: { ...saved.data, worker: ownName ?? null, cleans } }
}

// ---- five days kept on the phone (docs/plans/offline/PLAN-v2.md §3; Liam 1 Oct: open with no signal for up to
// 3 days). Today and the next four days, in one request, split into the same saved copy each day's own read makes,
// so a phone last online on Friday still has Monday and Tuesday. Refreshed after each load and every 15 minutes
// while Roundly is open, never while opened with no signal (nothing is asked of the server then).
const WINDOW_DAYS = 5
const WINDOW_EVERY_MS = 15 * 60_000
let windowSavedFor = ''
let windowSavedAt = 0
// One download at a time, in order: each one's "stored" message to the server must describe the days the phone
// holds after it, never an earlier answer that happened to arrive last.
let windowRun: Promise<void> = Promise.resolve()

function saveWindow(force = false): Promise<void> {
  windowRun = windowRun.then(() => downloadWindow(force)).catch(() => undefined)
  return windowRun
}

async function downloadWindow(force: boolean): Promise<void> {
  if (isLocalOnly() || (typeof navigator !== 'undefined' && navigator.onLine === false)) return
  const from = todayISO()
  const to = addDaysISO(from, WINDOW_DAYS - 1)
  // Whose list, and where each day is saved, fixed BEFORE asking: switching Everyone / Just mine while this is on
  // its way must not file one view's answer under the other's days (Astra, 1 Oct).
  const view = whoParam()
  const keys = Array.from({ length: WINDOW_DAYS }, (_, i) => [addDaysISO(from, i), cacheKeyFor(addDaysISO(from, i))] as const)
  const who = `${from}|${view}`
  if (!force && who === windowSavedFor && Date.now() - windowSavedAt < WINDOW_EVERY_MS) return
  let data: TodayPayload
  try {
    data = await api<TodayPayload>(`/api/today?from=${from}&to=${to}${view}`)
  } catch {
    // No signal or a refusal: the days already saved stay as they are.
    return
  }
  const at = Date.now()
  // What the phone now holds for each day: this answer, or a newer copy a single-day read already saved (never
  // put back to an older one, day-copies.ts). Only days really held are reported.
  const revs: Record<string, number> = {}
  for (const [date, key] of keys) {
    const held = saveDayCopy(key, { ...data, from: date, to: date, cleans: data.cleans.filter((c) => c.date === date) }, date)
    if (held.rev !== null) revs[date] = held.rev
  }
  windowSavedFor = who
  windowSavedAt = at
  // Compared straight away, while the answer is still this person's (nothing awaited since it arrived).
  if (!isOwnerToday()) noteJobChanges(data, at)
  // Stored: tell the server which copy of each day this phone now holds, so the owner's diary can say whether a
  // change has reached it (migration 0038; docs/plans/offline/PLAN-v3-changes.md §6). Only the days whose save
  // went through, and under the name the jobs were downloaded for. Best effort: if it doesn't arrive, the next
  // download says it again.
  if (Object.keys(revs).length > 0) {
    try {
      await postNow('/api/offline/ack', { device_id: deviceId(), revs, worker: data.worker ?? null })
    } catch {
      // said again next time
    }
  }
}

// After this phone's own changes reach Roundly, download the days again straight away, so its "stored" message
// catches up with them and the owner's diary doesn't say a change of the worker's own hasn't reached his phone.
let sentWatch = false
let pendingSends = 0
let resendTimer = 0
function watchOwnSends(): void {
  if (sentWatch) return
  sentWatch = true
  onQueueChange(() => {
    const now = waitingChanges().pending.size
    const dropped = now < pendingSends
    pendingSends = now
    if (!dropped) return
    window.clearTimeout(resendTimer)
    resendTimer = window.setTimeout(() => void saveWindow(true), 1500)
  })
}

// ---- "Your jobs changed since Fri 17:40" (docs/plans/offline/PLAN-v2.md §5; jobs-changed.ts) --------------------
// Workers only. Three saved records, each per signed-in person like every saved copy (and gone at log-out with
// them): the days as the worker last had them, the newest download, and the card. The card stays until he taps
// OK; then what he has been shown becomes the new starting point.
const SEEN_KEY = 'jobs-seen'
const LATEST_KEY = 'jobs-latest'
const CARD_KEY = 'jobs-changed'

function noteJobChanges(data: TodayPayload, at: number): void {
  const now = seenOf(data, at)
  const base = readCache<SeenWindow>(SEEN_KEY)?.data
  if (!isSeenWindow(base)) {
    saveCache(SEEN_KEY, now)
    return
  }
  const changes = diffWindows(base, now, ownFields(getQueue()))
  if (changes.length === 0) {
    // Nothing to tell (or the owner put it all back): the starting point moves on and any card goes.
    saveCache(SEEN_KEY, now)
    saveCache(CARD_KEY, null)
  } else {
    saveCache(LATEST_KEY, now)
    saveCache(CARD_KEY, { v: 1, since: base.at, changes } satisfies ChangedCard)
  }
  drawJobsChanged()
}

// The card fades in once when what it says is new, and the stops it names take one lemon tint (the movement set); a redraw
// of the same card replays nothing.
let changedSeen = ''
function drawJobsChanged(): void {
  const day = document.querySelector<HTMLElement>('#today-day')
  if (!day) return
  day.querySelector('.jobs-changed')?.remove()
  for (const c of day.querySelectorAll('.today-clean.job-changed')) c.classList.remove('job-changed')
  if (isOwnerToday()) return
  const card = readCache<ChangedCard>(CARD_KEY)?.data
  if (!isChangedCard(card)) { changedSeen = ''; return }
  const fresh = JSON.stringify(card.changes) !== changedSeen
  changedSeen = JSON.stringify(card.changes)
  const box = el('section', { class: 'jobs-changed', role: 'status' })
  const list = el('ul', { class: 'jobs-changed-list' })
  for (const c of card.changes) list.append(el('li', { class: `jobs-changed-${c.kind}` }, changeLine(c)))
  const ok = el('button', { type: 'button', class: 'btn-primary' }, 'OK')
  ok.addEventListener('click', () => {
    const latest = readCache<SeenWindow>(LATEST_KEY)?.data
    if (isSeenWindow(latest)) saveCache(SEEN_KEY, latest)
    saveCache(CARD_KEY, null)
    drawJobsChanged()
  })
  box.append(el('h3', {}, cardTitle(card)), list, ok)
  if (fresh) box.classList.add('is-new')
  day.prepend(box)
  // The jobs on the day showing, picked out until OK.
  for (const c of card.changes) {
    if (c.kind === 'removed' || c.date !== selectedDate) continue
    const stop = day.querySelector<HTMLElement>(`.today-clean[data-clean-id="${c.id}"]`)
    stop?.classList.add('job-changed')
    if (fresh) stop?.classList.add('is-new')
  }
}

// ---- work done on this phone that hasn't sent yet (docs/plans/offline/PLAN-v2.md §4) ----------------------------
// Every Today change to a job is a PATCH /api/cleans/<id> in the queue, which survives the phone closing Roundly.
// The day is drawn from the saved copy (or the server's answer) WITH those waiting changes laid on top, in the
// order they were made, so a job finished and paid with no signal still shows finished and paid after a restart,
// marked "Waiting to send". A change the server refused (the owner moved or changed the job meanwhile) is not laid
// on; the card says what was recorded and that it couldn't send, and keeps it until the worker dismisses it.

interface Waiting {
  pending: Set<number>
  refused: Map<number, { key: string; label: string; error: string }[]>
}

function waitingChanges(): Waiting {
  const pending = new Set<number>()
  const refused = new Map<number, { key: string; label: string; error: string }[]>()
  for (const q of getQueue()) {
    const m = q.method === 'PATCH' ? CLEAN_PATH.exec(q.path) : null
    if (!m) continue
    const id = Number(m[1])
    if (q.status === 'conflict' || q.status === 'failed') {
      const list = refused.get(id) ?? []
      list.push({ key: q.key, label: q.label, error: q.error ?? '' })
      refused.set(id, list)
    } else pending.add(id)
  }
  return { pending, refused }
}

function withWaiting(data: TodayPayload): TodayPayload {
  // Prices switched off for this worker: none shown, even from a day this phone saved before the switch (hidden-prices.ts).
  const shown = pricesHidden() ? withoutPrices(data) : data
  const laid = layWaiting(shown.cleans, getQueue())
  // A waiting Whose job: the job moves to that person's group at once, as the server will put it (phone_of follows a
  // visit's own worker).
  const cleans = laid.map((c) => (c.worker?.trim() && c.phone_of !== undefined && c.phone_of?.trim() !== c.worker.trim() ? { ...c, phone_of: c.worker.trim() } : c))
  return laid === shown.cleans && cleans.every((c, i) => c === laid[i]) ? shown : { ...shown, cleans }
}

// After the day is drawn: "Waiting to send" on jobs with changes still on the phone; and, above the day, every
// change Roundly refused because the job was changed first, whichever day or job it was, with what was recorded,
// kept until the worker lets it go (Astra, 1 Oct: the job itself may be gone from this phone's list).
// A job that was waiting to send when the day was last drawn and has gone since says "Sent" for a moment (the movement
// set: no endless spinner, and only what is known; a refused change says so above the day instead).
// Each change by its own queue key: a job says "Sent" only when every change that was waiting on it landed, never when one
// was let go (Astra).
let lastPending = new Map<number, string[]>()
const landed = new Set<string>()
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('roundly:queued-landed', (e) => {
    const d = (e as CustomEvent<{ key?: string; method?: string; path?: string }>).detail
    if (d?.key && d.method === 'PATCH' && d.path && CLEAN_PATH.test(d.path)) landed.add(d.key)
  })
}
function markWaiting(): void {
  const pending = new Map<number, string[]>()
  const refusedIds = new Set<number>()
  for (const q of getQueue()) {
    const m = q.method === 'PATCH' ? CLEAN_PATH.exec(q.path) : null
    if (m && q.status !== 'conflict' && q.status !== 'failed') pending.set(Number(m[1]), [...(pending.get(Number(m[1])) ?? []), q.key])
    else if (m) refusedIds.add(Number(m[1]))
  }
  for (const id of pending.keys()) {
    const card = document.querySelector<HTMLElement>(`#today-day .today-clean[data-clean-id="${id}"]`)
    if (card && !card.querySelector('.waiting-line')) card.append(el('p', { class: 'waiting-line' }, 'Waiting to send. It sends by itself when there\u2019s signal.'))
  }
  for (const [id, keys] of lastPending) {
    if (pending.has(id) || refusedIds.has(id) || !keys.every((k) => landed.has(k))) continue
    const card = document.querySelector<HTMLElement>(`#today-day .today-clean[data-clean-id="${id}"]`)
    if (!card || card.querySelector('.waiting-sent')) continue
    const sent = el('p', { class: 'waiting-sent', role: 'status' }, 'Sent')
    card.append(sent)
    window.setTimeout(() => sent.remove(), 2500)
  }
  const stillWaiting = new Set([...pending.values()].flat())
  for (const k of Array.from(landed)) if (!stillWaiting.has(k)) landed.delete(k)
  lastPending = pending
  const refused = getQueue().filter((q) => q.method === 'PATCH' && CLEAN_PATH.test(q.path) && (q.status === 'conflict' || q.status === 'failed'))
  const day = document.querySelector<HTMLElement>('#today-day')
  day?.querySelector(':scope > .refused-list')?.remove()
  if (!day || refused.length === 0) return
  const box = el('section', { class: 'refused-list', role: 'status' })
  box.append(
    el('h3', {}, refused.length === 1 ? 'This didn\u2019t send' : `${refused.length} things didn\u2019t send`),
    el('p', { class: 'refused-help' }, 'The job was changed before this reached Roundly, so Roundly kept the owner\u2019s version. Pass this on to the owner, then let it go.'),
  )
  for (const q of refused) {
    const when = new Date(q.createdAt)
    const what = describeChange(q.body)
    const row = el('div', { class: 'refused-row' })
    row.append(
      el('strong', {}, q.label),
      el('span', {}, `${what.length ? what.join(' · ') : 'A change'} · recorded ${dayLabel(todayISOOf(when))} ${clockLabel(when.getTime())}`),
    )
    const drop = el('button', { type: 'button', class: 'link-button' }, 'Let it go')
    drop.addEventListener('click', () => {
      if (!window.confirm('Let this go? It won\u2019t be sent, and it can\u2019t be got back.')) return
      dismiss(q.key)
      row.remove()
      if (!box.querySelector('.refused-row')) box.remove()
    })
    row.append(drop)
    box.append(row)
  }
  day.prepend(box)
}

function todayISOOf(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

let windowTimer = 0
function keepWindowFresh(): void {
  watchOwnSends()
  if (windowTimer) return
  windowTimer = window.setInterval(() => void saveWindow(), WINDOW_EVERY_MS)
  // Signal back after opening with none (me.ts): save the days straight away.
  window.addEventListener('dropby:signal-back', () => void saveWindow(true))
}

// A changed leaving time or a voice note being recorded is never drawn over by a read: it tries again shortly (Astra).
const LOAD_HOLD = '#today-day :is(.time-chooser-asked[data-edited], .voice-recording, .voice-starting)'
let loadRetry = 0
function loadHeld(): boolean {
  if (!document.querySelector(LOAD_HOLD)) return false
  window.clearTimeout(loadRetry)
  // Only while Today is still the page on show (Astra).
  const gen = routeGen
  loadRetry = window.setTimeout(() => { if (gen === routeGen && document.querySelector('#today-day')) void load() }, 5000)
  return true
}
async function load(): Promise<void> {
  // A stop's sheet open: read again when it closes, so it never vanishes mid-answer (Astra).
  if (document.querySelector('#today-day dialog.stop-sheet[open], dialog.time-sheet[open]')) { reloadWaiting = true; return }
  if (loadHeld()) return
  const sequence = ++requestSequence
  const gen = routeGen
  const root = document.querySelector<HTMLElement>('#screen')!
  // Known before the heading is drawn: the "Everyone / Just mine" switch
  // depends on it.
  if (ownName === undefined) ownName = ownerDoesJobs() ? (getMe()?.workerName?.trim() ?? null) : null
  // A one-person round's owner cannot clear their name (0078), and a new name carries their visits with it, so their
  // Today follows the name as the last sign-in check knows it.
  else if (worksAlone() && getMe()?.workerName?.trim()) ownName = getMe()!.workerName!.trim()
  // The same day read again (after a save, or the phone coming back) keeps its jobs on screen until the answer comes,
  // so a note just saved stays in sight (Liam, 13 Oct 2026: "The message we add should instanly appear so they know
  // its added"); the page used to go blank while it asked.
  const shown = root.querySelector<HTMLElement>('#today-day')
  const keep = shown && shown.dataset.day === `${selectedDate}${whoParam()}` ? [...shown.childNodes] : null
  renderShell(root)
  if (keep) root.querySelector<HTMLElement>('#today-day')?.replaceChildren(...keep)
  try {
    // A worker's list is theirs by sign-in; an owner's Today tab asks for the
    // name on their own visits (ownerDoesJobs), or it would be the whole round.
    // Held from the first load until the page reloads (Grok review
    // 2026-09-25): a later sign-in check that renamed or cleared it must not
    // turn this into the whole round, or an empty day, mid-shift.
    // Saved through Today's own path (day-copies.ts): an answer older than the copy already on the phone (the
    // window save got there first) neither replaces it nor is shown over it. The day and where it is saved are
    // fixed before asking, as the read itself used to fix them.
    const [day, key] = [selectedDate, todayCacheKey()]
    // The phone's noticed arrivals and leavings (timer nudges, 0083) with it: "When did you start cleaning?" offers them.
    const [fresh] = await Promise.all([api<TodayPayload>(todayPath()), readNoticed(todayISO())])
    const data = saveDayCopy(key, fresh, day).newer ?? fresh
    if (gen !== routeGen || sequence !== requestSequence) return
    if (document.querySelector('#today-day dialog.stop-sheet[open], dialog.time-sheet[open]')) { reloadWaiting = true; return }
    if (loadHeld()) return
    renderDay(withWaiting(data), null)
    markWaiting()
    drawJobsChanged()
    if (focusId !== null) openAtNextDue = false
    showFocus()
    if (cardAsk) void runCardAsk(data)
    openAtNext()
    keepWindowFresh()
    void saveWindow()
  } catch (err) {
    if (gen !== routeGen || sequence !== requestSequence) return
    // A one-person round whose phone last saved the day under Everyone (before the round chose Just me): that copy,
    // with only the jobs that were on the owner's phone or on nobody's, so a former worker's jobs never count as theirs
    // in the tally or Finish my day (Sol's review).
    const cached = readCache<TodayPayload>(todayCacheKey()) ?? (worksAlone() ? ownFromEveryone(readCache<TodayPayload>(`today:everyone:${selectedDate}`)) : null)
    if (cached && document.querySelector('#today-day dialog.stop-sheet[open], dialog.time-sheet[open]')) { reloadWaiting = true; return }
    if (cached && loadHeld()) return
    if (cached) {
      renderDay(withWaiting(cached.data), cached.at)
      markWaiting()
      drawJobsChanged()
      // A link to a job opens on it with no signal too (Astra).
      if (focusId !== null) openAtNextDue = false
      showFocus()
      if (focusId === null) openAtNext()
      // A card link with no signal acts on the day saved on the phone, once (GPT-6.1's review).
      if (cardAsk) void runCardAsk(cached.data)
      loadError(root, (err as Error).message, () => void load(), cached.at, 'Your jobs')
    } else {
      // Nothing to act on: the link's question is not kept for a later visit (Sonnet's review).
      cardAsk = null
      markWaiting() // refused changes show even on a day with nothing saved
      loadError(root, (err as Error).message, () => void load())
    }
  }
}

// ---- strip + day frame -------------------------------------------------------

// The phone comes back to the front (unlocked, or switched back to): read the
// day again, so a job the owner added or moved while it sat in a pocket shows
// up (worker review 2026-09-24: nothing refreshed it but a returning signal).
// If the date has turned since the screen was drawn and it was showing that
// day, it moves on to the new today. Not while something on a card would be
// lost by drawing it again: a voice note recording, sending or waiting to be
// sent again, an open chooser or menu, or a photo waiting for its label.
let drawnToday = todayISO()
let resumeBound = false
function bindResume(): void {
  if (resumeBound) return
  resumeBound = true
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return
    const day = document.querySelector('#today-day')
    if (!day) return // another screen is showing
    // A red "not sent" bar is no reason to wait: the photo or note is kept in
    // memory and on the phone, and comes back on the redrawn card.
    if (day.querySelector('.voice-recording, .voice-starting, .voice-note-btn:disabled, .blocked-chooser, .late-chooser, .time-chooser:not(.time-chooser-asked), .time-chooser-asked[data-edited], details.live-more[open], .photo-pick:not([hidden]), .access-note-editor, dialog.stop-sheet[open]')) return
    const now = todayISO()
    if (now !== drawnToday && selectedDate === drawnToday) selectedDate = now
    void load()
  })
}

function renderShell(root: HTMLElement): void {
  drawnToday = todayISO()
  clear(root)
  const strip = el('div', { class: 'day-strip', role: 'tablist', 'aria-label': 'Pick a day' })
  const today = todayISO()
  for (let offset = -STRIP_BACK; offset <= STRIP_AHEAD; offset++) {
    const date = addDaysISO(today, offset)
    const d = new Date(`${date}T12:00:00`)
    const btn = el(
      'button',
      { class: 'strip-day', role: 'tab', 'aria-selected': String(date === selectedDate) },
      el('span', { class: 'strip-dow' }, date === today ? 'Today' : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()]),
      el('span', { class: 'strip-dom' }, String(d.getDate())),
    )
    if (date === today) btn.classList.add('strip-today')
    if (date === selectedDate) btn.classList.add('strip-selected')
    btn.addEventListener('click', () => goToDay(date))
    strip.append(btn)
  }
  const heading = el('div', { class: 'today-heading' }, el('h2', {}, dayLabel(selectedDate)))
  if (isOwnerToday() && ownName && !worksAlone()) {
    const sw = el('div', { class: 'today-view-switch', role: 'group', 'aria-label': 'Whose jobs' })
    for (const [value, label] of [['everyone', 'Everyone'], ['mine', 'Just mine']] as const) {
      const b = el('button', { type: 'button', 'aria-pressed': String(ownerView === value) }, label)
      b.addEventListener('click', () => {
        if (ownerView === value) return
        ownerView = value
        try {
          localStorage.setItem(VIEW_KEY, value)
        } catch {
          // kept for this visit only
        }
        void load()
      })
      sw.append(b)
    }
    heading.append(sw)
  }
  heading.append(strip)
  root.append(heading)
  // Worker notes to look at (0071), for an owner, while any wait.
  if (isOwnerToday()) root.append(workerNotesBar())
  root.append(swipeable(el('div', { id: 'today-day', 'data-day': `${selectedDate}${whoParam()}` })))
  // Job reminders on this phone (reminders.ts) and Timer nudges, a test (timer-nudges.ts), below the day's jobs, for a
  // worker only. An owner sets them once in More > Extra features, under This phone (Liam, 2 Nov 2026: "Not sure these
  // should go here?"); a worker never reaches Extra features, so theirs stay here.
  if (!isOwnerToday()) {
    const reminders = remindersRow()
    if (reminders) root.append(reminders)
    const nudges = nudgesRow()
    if (nudges) root.append(nudges)
  }
  // The strip opens with TODAY as its first day (Liam, 2026-09-23: it opened
  // on the day a week ago, so today was off the right edge). The past week is
  // still there by scrolling back. Looking at an earlier day, that day leads
  // instead, so the one picked is always in view.
  const lead = strip.querySelector<HTMLElement>(selectedDate < today ? '.strip-selected' : '.strip-today')
  if (lead) strip.scrollLeft = lead.offsetLeft - strip.offsetLeft
  // A day picked further along than the strip shows (after a swipe) is
  // brought into view at the right-hand end.
  const picked = strip.querySelector<HTMLElement>('.strip-selected')
  if (picked) {
    const right = picked.offsetLeft - strip.offsetLeft + picked.offsetWidth
    if (right > strip.scrollLeft + strip.clientWidth) strip.scrollLeft = right - strip.clientWidth + 4
  }
}

// ---- visit cards -------------------------------------------------------------

// ---- moving between days ---------------------------------------------------------

// One way to change day, from a strip tap or a swipe. A photo still waiting
// to send stays with its visit when the day changes (the draft is keyed by
// visit id), but the worker should know it has not gone up yet — say so,
// then move. The new day's jobs slide in from the side it lies on
// (Liam, 2026-09-24).
let slideFrom: 'left' | 'right' | null = null
function goToDay(date: string): void {
  if (selectedDate === date) return
  const today = todayISO()
  if (date < addDaysISO(today, -STRIP_BACK) || date > addDaysISO(today, STRIP_AHEAD)) return
  if (hasPendingPhotoDrafts()) toast('A photo has not been sent yet. It stays waiting on its visit.')
  slideFrom = date > selectedDate ? 'right' : 'left'
  selectedDate = date
  void load()
}

function playDaySlide(container: HTMLElement): void {
  const from = slideFrom
  slideFrom = null
  if (!from || reducedMotion() || typeof container.animate !== 'function') return
  const x = from === 'right' ? 48 : -48
  container.animate([{ transform: `translateX(${x}px)`, opacity: 0 }, { transform: 'none', opacity: 1 }], { duration: 280, easing: 'cubic-bezier(.2,.8,.2,1)' })
}

// Swipe left for the next day, right for the day before (Liam, 2026-09-24).
// Only a clear sideways flick counts: longer than 70px, at least twice as
// wide as it is tall, inside 700ms. Not when it starts on something that
// moves sideways itself (the slide-to-finish bar, the day strip, the photos)
// or on anything being typed or chosen in. Up-and-down scrolling is the
// browser's (touch-action: pan-y on the list).
const NO_SWIPE = '.slide-track, .day-strip, .photo-strip, input, textarea, select, .late-chooser, .blocked-chooser, .time-chooser, .live-more, .photo-pick, .live-notes, .access-note-editor, .access-note-row, .stop-sheet, .note-clip, .today-tiles'
function swipeable(day: HTMLElement): HTMLElement {
  let start: { x: number; y: number; at: number; id: number } | null = null
  day.addEventListener('pointerdown', (e) => {
    if (e.pointerType !== 'touch') return
    if ((e.target as Element | null)?.closest(NO_SWIPE)) return
    start = { x: e.clientX, y: e.clientY, at: Date.now(), id: e.pointerId }
  })
  day.addEventListener('pointercancel', () => {
    start = null
  })
  day.addEventListener('pointerup', (e) => {
    const s = start
    start = null
    if (!s || e.pointerId !== s.id) return
    const dx = e.clientX - s.x
    const dy = e.clientY - s.y
    if (Math.abs(dx) < 70 || Math.abs(dx) < Math.abs(dy) * 2 || Date.now() - s.at > 700) return
    // Not away from a reason or a late text half chosen, or a note half edited, on any card.
    if (day.querySelector('.blocked-chooser, .late-chooser, .access-note-editor')) return
    // In Focus mode a swipe moves to the next or previous stop, not another day (F1).
    if (focusMode && day.querySelector('.is-focus-list')) { moveFocus(dx < 0 ? 1 : -1); return }
    goToDay(addDaysISO(selectedDate, dx < 0 ? 1 : -1))
  })
  return day
}

// The address as a maps link: a worker at the gate needs navigation, not the
// customer page (which they cannot open — see the middleware scope).
// Straight into the route from where the phone is (Liam, 2026-09-24): a
// search only dropped a pin, and Maps then needed Directions and Start. The
// place is the address without any "DO NOT BOOK 11-1PM" note glued to it
// (that went to Google as part of the search), plus its postcode.
function placeOf(cl: TodayClean): string {
  return cl.address ? fullAddress(cl, splitAccessWarning(cl.address).addr).trim() : (cl.postcode ?? '').trim()
}
// Their exact spot when one is pinned (Liam, 27 Oct 2026: "google maps often take to wrong address"; maps-link.ts).
function mapsHref(cl: TodayClean): string | null {
  return directionsHref(cl.maps_link, placeOf(cl))
}

// The rest of today as one route, in the day's order, from where the phone is
// (review 2026-09-24): after each job the worker otherwise had to open the
// next card and start again. Google Maps on a phone takes a destination and at
// most three stops on the way, so the route covers the next four jobs.
const ROUTE_MAX = 4
function routeHref(stops: TodayClean[]): string | null {
  const places = stops.map((cl) => { const s = placeInMapsLink(cl.maps_link); return s ? `${s.lat},${s.lng}` : placeOf(cl) }).filter(Boolean).slice(0, ROUTE_MAX)
  if (places.length < 2) return null
  const destination = places[places.length - 1]
  const via = places.slice(0, -1).join('|')
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}&waypoints=${encodeURIComponent(via)}&travelmode=driving`
}

// The notes as lines, each with Edit for the owner (src/today-notes.ts, 13 Oct 2026); the day is read again once one is
// saved, removed or undone.
// The day read again with the page held where it was (Liam, 13 Oct 2026: after hiding a note "it takes back up to the
// top of the page, doesn't hold where we currently are"): Today draws itself afresh, so the place is put back after.
async function reloadInPlace(): Promise<void> {
  const y = window.scrollY
  await load()
  requestAnimationFrame(() => window.scrollTo({ top: y, behavior: 'auto' }))
}

// The customer's name opens their page, for the owner only (Liam, 13 Oct 2026: "the name on the tile should go to the
// customer if they are the owner like me but a worker like sendi shouldn't be able to ... the road is the most important
// thing to be clickable, name is just nice to have and i dont want them competing"). It keeps the name's own look with a
// small arrow after it (the lemon underline stays the address's), and only the words themselves take the tap.
// Whole pounds on Today, as on the Diary (Liam, 4 Nov 2026, design H: "£25", not "£25.00"); pence stay when there are
// any (https://claude.ai/artifact/74zWf8kUcfCMSk2dJpPpKE).
const pounds = (p: number): string => money(p).replace(/\.00$/, '')
const wholeText = (s: string): string => s.replace(/([£$€][\d,]+)\.00(?!\d)/g, '$1')
function wholePounds<T extends HTMLElement>(n: T): T {
  for (const t of [n, ...Array.from(n.querySelectorAll<HTMLElement>('span'))]) if (!t.children.length && t.textContent) t.textContent = wholeText(t.textContent)
  return n
}
// Still owed on a finished visit: its unpaid balance after credit and anything paid in (brought.ts toCollect), never
// its price less what was taken, so credit, part payments and another house's overpayment can't make or hide a debt
// (Astra, 6.1 and Sonnet, 4 Nov 2026). Nothing for a visit marked paid, not yet recorded, or in an opening balance.
const owedOn = (c: TodayClean): number => (c.state === 'done' && (c as { carryover?: number | null }).carryover !== 1 && c.payment_status === 'unpaid' ? toCollect(c) ?? 0 : 0)
// The price on the stop's own line, after the name (design H: time, name and price on one line that never wraps). A
// visit with insides keeps its Outside / Inside tags under the street.
function linePrice(cl: TodayClean, owner: boolean): { price: HTMLElement | null; rest: HTMLElement[] } | null {
  if (cl.amount_pence == null || pricesHidden()) return null
  if (hasInsides(cl)) return { price: null, rest: owner ? [priceButton(cl, { className: 'badge', after: () => void reloadInPlace() }), ...extraBadges(cl.extras_json), ...askedBadges(cl.extras_asked_json)] : priceBadges(cl) }
  if (owner) return { price: el('span', { class: 'stop-line-price' }, wholePounds(priceButton(cl, { className: 'badge', after: () => void reloadInPlace() }))), rest: [...extraBadges(cl.extras_json), ...askedBadges(cl.extras_asked_json)] }
  const [first, ...rest] = priceBadges(cl)
  return { price: first ? el('span', { class: 'stop-line-price' }, wholePounds(first)) : null, rest }
}

// Tap a time (Liam, 4 Nov 2026, design H with J's quick changes): the owner taps a stop's time, or "Any time", and the
// Diary's own time sheet opens (visit-time.ts), with Message them and Whose job under it. Only a visit still to do, on
// today or a day to come; a worker's phone keeps the time as words (the server refuses their time and worker changes).
function stopTime(cl: TodayClean): HTMLElement | null {
  if (!isOwnerToday() || cl.state !== 'booked' || cl.started_at || cl.finished_at || cl.date < todayISO() || !setTimesOn()) return null
  const shown = shownVisitTime(cl)
  const b = el('button', { type: 'button', class: `clean-time stop-time-btn${shown ? '' : ' is-any'}`, 'aria-label': `${shown ? `Change the time, ${shown},` : 'Set a time'} for ${cl.customer_name}` }, shown ?? 'Any time')
  b.addEventListener('click', (e) => {
    e.stopPropagation()
    // The parking warning reads the customer's parking hours as the Diary has them (customer_parking).
    openTimePicker({ ...cl, kind: cl.kind ?? 'window', customer_parking: cl.parking_restrictions ?? null } as unknown as Parameters<typeof openTimePicker>[0], b,
      { changed: () => void reloadInPlace(), drawNow: drawWaitingNow, extra: (close) => timeSheetExtras(cl, close) })
  })
  return b
}
// Under the times: Message them about the visit, and whose job it is (You, then the people who sign in). Changing whose
// job it is saves the visit's own person, with Undo; nobody is texted by it.
function timeSheetExtras(cl: TodayClean, close: () => void): HTMLElement {
  const box = el('div', { class: 'today-time-extra' })
  const first = cl.customer_name.trim().split(/\s+/)[0] || 'them'
  const msg = el('button', { type: 'button', class: 'btn-secondary today-time-message' }, `Message ${first}`)
  msg.addEventListener('click', () => { close(); askAboutDay(cl.customer_id, cl.date, { visitId: cl.id, onDone: () => void reloadInPlace() }) })
  box.append(msg)
  // The people who sign in come with Everyone; remembered from it so Just mine offers them too (Astra, 6.1, Sonnet).
  if (lastDrawn?.data.people?.length) knownPeople = lastDrawn.data.people
  const names: string[] = []
  for (const n of [ownName ?? '', ...knownPeople]) if (n.trim() && !names.some((x) => x.toLowerCase() === n.trim().toLowerCase())) names.push(n.trim())
  // Just mine has no phone_of: every job on it is the owner's own.
  const current = (cl.phone_of ?? cl.worker ?? (showingMine() ? ownName ?? '' : '')).trim().toLowerCase()
  if (!worksAlone() && names.length > 1) {
    const row = el('div', { class: 'booking-chips today-time-whose', role: 'group', 'aria-label': 'Whose job' })
    for (const n of names) {
      const mine = !!ownName && n.toLowerCase() === ownName.toLowerCase()
      const on = n.toLowerCase() === current
      const b = el('button', { type: 'button', class: 'booking-chip', 'aria-pressed': String(on) }, mine ? 'You' : n)
      b.addEventListener('click', () => { if (on) return; close(); setWhose(cl, n) })
      row.append(b)
    }
    box.append(el('p', { class: 'today-time-label' }, 'Whose job'), row)
  }
  return box
}
let knownPeople: string[] = []
// Drawn at once with the change laid over the day, with or without signal (the Diary's drawNow).
function drawWaitingNow(): void { if (lastDrawn) renderDay(withWaiting(lastDrawn.data), lastDrawn.cachedAt) }
function setWhose(cl: TodayClean, name: string): void {
  const was = cl.worker?.trim() ? cl.worker : null
  const mine = !!ownName && name.toLowerCase() === ownName.toLowerCase()
  const saving = mutate({ label: `Whose job: ${cl.customer_name} to ${name}`, method: 'PATCH', path: `/api/cleans/${cl.id}`, body: { version: cl.version, worker: name },
    onAck: () => void reloadInPlace(), onConflict: () => void reloadInPlace() })
  drawWaitingNow()
  void saving.then((ack) => {
    if (!ack.ok && ack.status >= 400 && ack.status < 500) { window.alert(ack.error ?? 'That did not save.'); void reloadInPlace(); return }
    undoPatch({ text: `${cl.customer_name} ${mine ? 'is yours' : `is ${name}'s`} today`, label: `Undo whose job: ${cl.customer_name}`, path: `/api/cleans/${cl.id}`, ack, version: cl.version,
      body: { worker: was }, after: () => void reloadInPlace() })
  })
}

function nameNode(customerId: number, text: string): HTMLElement {
  if (!isOwnerToday()) return el('strong', { class: 'today-name' }, text)
  return el('a', { class: 'today-name today-name-link', href: `#/customer/${customerId}`, 'aria-label': `${text}: open their page` }, text)
}

function noteRows(cl: TodayClean, tag: 'p' | 'span' = 'p'): HTMLElement[] {
  return accessNoteItems(cl).map((n) => noteRow(n, cl, isOwnerToday(), () => void reloadInPlace(), tag))
}

// The address with its postcode, unless the address already carries it.
function fullAddress(cl: TodayClean, addr: string): string {
  const squash = (x: string): string => x.toUpperCase().replace(/\s+/g, '')
  return cl.postcode && !squash(addr).includes(squash(cl.postcode)) ? `${addr}, ${cl.postcode}` : addr
}

// The card follows the diary's job row (Liam, 2026-09-23: "closer match ...
// the schedule page"): the slate time chip and the name on one line, the same
// badges, then what only a worker needs. The address is the line tapped most,
// so it is the biggest thing on the card, underlined in the brand lemon, and
// opens the map. Then one lemon action (Start / Finish / Done), a row of
// drawn-icon tiles (Directions · Call · Voice note · Photo), and the two
// things that go wrong (Couldn't do it · Running late) quietly underneath.
// Which day a card belongs to decides what can be done on it (worker review,
// 2026-09-24): TODAY is the working day; a PAST day can still be logged (a
// round that runs past midnight, or the cash written up next morning — the
// server accepts Done for any day up to today) but not started; a FUTURE day
// is for reading and getting there only (the server refuses Done on a day
// that has not come).
type When = 'past' | 'today' | 'future'

// What the job is, in the diary's words, or by its own name once several jobs is on (step 2): "Gutters".
function jobWord(cl: TodayClean): string {
  return severalJobsOn() && cl.job_name?.trim() ? cl.job_name.trim() : kindLabel(cl.kind)
}

// ---- design B: the day as numbered stops ------------------------------------------------------------------------------
// Liam, 24 Oct 2026 ("I quite like B"; the drawings he approved, boards B1 to B12 and E1 to E9:
// https://claude.ai/artifact/QKN21svewxgyPb6SYRqWnd). The day's stops sit on one numbered line. One stop is open at a
// time, the next one to do or the one tapped: its full address, one line of notes (a tap shows the rest), Start job and
// three small buttons (Directions, Contact, ⋯). The others fold to a line with a round tick, which opens Job done as Done
// always has (nothing is saved before Save, and it starts on Not paid). ⋯ opens the stop's sheet: Done, Started a while
// ago?, Voice note, Photo, Running late, Couldn't do it, and the owner's Change price and Their page. Every button the
// card had is kept; only where it sits has changed (Astra's check of the drawings against the app, 24 Oct 2026).
interface StopPlace { n: number | string | null; open: boolean; focus?: boolean }

// The stop's number on the line: lemon to do, grey when it couldn't be done. A finished job shows its green tick instead.
function stopNum(n: number | string, blocked: boolean): HTMLElement {
  return el('span', { class: `stop-num${blocked ? ' is-blocked' : ''}`, 'aria-hidden': 'true' }, String(n))
}

// The round tick on a stop: Job done, the panel Done always opened (Liam: "Job done stays exactly as today").
function tickButton(cl: TodayClean, openDone: () => void, inHouse: boolean): HTMLButtonElement {
  const b = el('button', { type: 'button', class: 'stop-tick', 'aria-label': `Done: ${inHouse ? `${jobWord(cl)} for ${cl.customer_name}` : cl.customer_name}` }) as HTMLButtonElement
  b.addEventListener('click', openDone)
  return b
}

// Something to know before going: the address's own warning, the customer's notes, From last time, or the visit's note
// (Liam: "a red ! flag on stops that have notes"; Astra: the visit's note and From last time count too).
function lastTimeNote(cl: TodayClean): string | null {
  if (!cl.last_time) return null
  try { return (JSON.parse(cl.last_time) as { note?: string | null }).note?.trim() || null } catch { return null }
}
// Warning or just a note (0088, src/note-kind.ts; Liam, 26 Oct 2026): what this stop's notes say about THIS visit.
function stopNotes(cl: TodayClean): StopNotes {
  return { addressWarn: cl.address ? splitAccessWarning(cl.address).warn : null, notes: accessNoteItems(cl).map((n) => ({ field: n.field as ChosenField, text: n.text })),
    choices: choicesOf(cl.note_kinds), visitNote: cl.note ?? null, lastTime: lastTimeNote(cl), time: visibleTime(cl), date: cl.date, lengthMinutes: cl.duration_minutes ?? null }
}
// Red "!" for something to watch for on this visit, a grey note picture for notes only (Sid's "Anthony Carters Dad").
function stopFlag(cl: TodayClean | TodayClean[]): HTMLElement | null {
  // A house is red when any of its unfinished jobs warns on its own visit (GPT-6.1's notes review 12).
  const marks = (Array.isArray(cl) ? cl.filter((c) => c.state !== 'done') : [cl]).map((c) => stopMark(stopNotes(c)))
  const mark = marks.includes('warn') ? 'warn' : marks.includes('note') ? 'note' : null
  if (mark === 'warn') return el('span', { class: 'stop-flag', role: 'img', 'aria-label': 'Something to watch for before going' }, '!')
  if (mark === 'note') return el('span', { class: 'stop-flag stop-flag-note', role: 'img', 'aria-label': 'A note, nothing to watch for' }, lineDrawing('note', 14))
  return null
}

// One clipped line of notes on an open stop (Liam: the gate code and the dog matter at the door, but the note must never
// make the stop long). A tap shows the rest, with the owner's Edit and Hide.
function noteClip(cl: TodayClean, row: HTMLElement, onTap?: () => void): HTMLElement | null {
  const warn = cl.address ? splitAccessWarning(cl.address).warn : null
  const last = lastTimeNote(cl)
  // Warnings first, so the dog is read before the rest (0088): each note's kind on this visit.
  // The same verdict as the stop's flag, so a dog in this visit's note is red here too (GPT-6.1's notes review 13).
  const s = stopNotes(cl)
  const kinds = stopLines({ ...s, visitNote: null, lastTime: null })
  const warnOf = (field: string): boolean => kinds.some((k) => k.field === field && k.warn)
  const visitWarns = stopLines({ ...s, addressWarn: null, notes: [], lastTime: null }).filter((l) => l.warn)
  const items = [
    ...(warn ? [{ text: warn, warn: true }] : []),
    ...accessNoteItems(cl).map((n) => ({ text: noteLine(n), warn: warnOf(n.field) })),
    ...visitWarns.map((l) => ({ text: `This visit: ${l.text}`, warn: true })),
    ...(last ? [{ text: `From last time: ${last}`, warn: guessKind(last, true).kind === 'warn' }] : []),
  ].sort((a, b) => Number(b.warn) - Number(a.warn))
  const words = items.map((i) => i.text).join(' · ')
  if (!words) return null
  const anyWarn = items.some((i) => i.warn)
  const b = el('button', { type: 'button', class: `note-clip${anyWarn ? '' : ' note-clip-plain'}`, 'aria-expanded': 'false', 'aria-label': `${anyWarn ? 'Something to watch for' : 'Notes'}: ${words}` },
    el('span', { class: 'note-clip-mark', 'aria-hidden': 'true' }, anyWarn ? '!' : lineDrawing('note', 13)),
    el('span', { class: 'note-clip-words' }, words),
    el('span', { class: 'note-clip-arrow', 'aria-hidden': 'true' }))
  b.addEventListener('click', () => {
    const open = onTap ? b.getAttribute('aria-expanded') !== 'true' : row.classList.toggle('notes-open')
    b.setAttribute('aria-expanded', String(open))
    onTap?.()
  })
  return b
}

// The stop's sheet (B5): rises from the bottom over the day. It lives inside the stop, so the lock screen card's links and
// "Running late for …" still find its buttons; a question asked in it (Running late, Couldn't do it) takes its place.
interface StopSheet { opener: HTMLButtonElement; open: () => void; close: () => boolean; held: () => boolean; dialog: HTMLDialogElement; body: HTMLElement; host: HTMLElement }
// The day read again while a sheet is open waits until it closes, so a sheet never vanishes under a finger.
let reloadWaiting = false
function stopSheet(row: HTMLElement, who: string, title: string, sub: string): StopSheet {
  const dialog = el('dialog', { class: 'stop-sheet', 'aria-label': title }) as HTMLDialogElement
  const shut = el('button', { type: 'button', class: 'stop-sheet-close', 'aria-label': 'Close' })
  const body = el('div', { class: 'stop-sheet-body' })
  const host = el('div', { class: 'stop-sheet-ask' })
  dialog.append(el('div', { class: 'stop-sheet-inner' },
    el('div', { class: 'stop-sheet-handle', 'aria-hidden': 'true' }),
    el('header', { class: 'stop-sheet-head' }, el('div', { class: 'stop-sheet-title' }, el('h3', {}, title), ...(sub ? [el('p', { class: 'stop-sheet-sub' }, sub)] : [])), shut),
    body, host))
  // Never put away with the microphone on: the recording would carry on out of sight (Astra). Its button stops it.
  const recording = (): boolean => !!dialog.querySelector('.voice-recording, .voice-starting')
  // A voice note recording keeps its button in sight: nothing covers it or takes the sheet away until it is stopped.
  const held = (): boolean => { if (!recording()) return false; toast('Stop the voice note first: tap it.'); return true }
  // Says whether it closed: whatever leaves the sheet for another (Done, Change price, Start job) waits for that.
  const close = (): boolean => {
    if (!dialog.open) return true
    if (held()) return false
    dialog.close()
    return true
  }
  dialog.addEventListener('cancel', (e) => { if (recording()) { e.preventDefault(); toast('Stop the voice note first: tap it.') } })
  shut.addEventListener('click', () => { close() })
  // A tap on the dimmed day around the sheet puts it away.
  dialog.addEventListener('click', (e) => { if (e.target === dialog) close() })
  if (typeof MutationObserver === 'function') new MutationObserver(() => dialog.classList.toggle('is-asking', host.childElementCount > 0)).observe(host, { childList: true })
  dialog.addEventListener('close', () => {
    host.replaceChildren()
    for (const c of Array.from(dialog.querySelectorAll('.time-chooser'))) c.remove()
    for (const l of Array.from(dialog.querySelectorAll<HTMLElement>('.job-earlier'))) l.hidden = false
    if (reloadWaiting) { reloadWaiting = false; void load() }
  })
  const open = (): void => {
    if (dialog.open || !dialog.isConnected) return
    // Its stop opens too, so what the sheet hands back (a photo's label, a question) shows on it (Astra).
    const owner = row.closest<HTMLElement>('.today-stops > .today-stop')
    if (owner) openStop(owner)
    if (typeof dialog.showModal === 'function') dialog.showModal()
    else dialog.setAttribute('open', '')
    // The sheet itself takes the focus, as Finish my day's does, so no button looks pressed as it opens.
    dialog.setAttribute('tabindex', '-1')
    dialog.focus({ preventScroll: true })
  }
  const opener = el('button', { type: 'button', class: 'stop-square stop-more ic ic-more', 'aria-label': `More for ${who}`, 'aria-haspopup': 'dialog' }, 'More') as HTMLButtonElement
  opener.addEventListener('click', open)
  row.append(dialog)
  return { opener, open, close, held, dialog, body, host }
}

// `inHouse`: one of two or more jobs at the same house on the day (several jobs, Liam Q9): the house above it carries the
// name, address and access notes once, and this card leads with the job's name instead.
function renderCard(cl: TodayClean, rerender: () => void, next?: TodayClean, inHouse = false, stop: StopPlace = { n: null, open: true }): HTMLElement {
  const row = el('div', { class: `clean-row clean-${cl.state} today-clean today-stop${inHouse ? ' today-house-job' : ''}` })
  row.dataset.cleanId = String(cl.id)
  // Still to do (not done, no "Couldn't do it"): where Today opens (openAtNext).
  if (cl.state === 'booked' && !cl.blocked_reason) row.dataset.toDo = '1'
  const today = todayISO()
  const when: When = cl.date < today ? 'past' : cl.date > today ? 'future' : 'today'
  // A finished job: green, paid or not (Liam, 14 Oct 2026: "the satisfaction of all tiles turning green once complete"),
  // its tick where its number was; its badge still says Not paid. The Diary keeps green for paid and amber for owed.
  if (cl.state === 'done') {
    row.classList.add('clean-paid', 'today-done')
    row.append(el('span', { class: 'done-mark ic ic-check', 'aria-hidden': 'true' }))
  }
  // A job started today and not finished is the lemon card (Liam kept it: "the yellow is really distinctive"). Not when
  // "Couldn't do it" has been recorded on it: that card must show the red line, and Finish is not the way out of a job
  // that did not happen (worker review 2026-09-24: the reason vanished, then Finish wiped it).
  if (when === 'today' && cl.state === 'booked' && cl.started_at && !cl.finished_at && !cl.blocked_reason) return renderLiveCard(cl, row, rerender, next, stop)
  row.classList.add(stop.open ? 'is-open' : 'is-folded')
  if (stop.focus) row.classList.add('is-focus')
  if (!inHouse && !stop.focus) row.append(stopOpener(row, `${cl.customer_name}, ${jobWord(cl)}`))
  if (stop.n !== null && cl.state !== 'done') row.append(stopNum(stop.n, cl.state === 'booked' && !!cl.blocked_reason))
  const main = el('div', { class: 'sched-clean-main' })
  const line = el('span', { class: 'sched-clean-line' })
  const timeNode = inHouse ? null : stopTime(cl)
  if (timeNode) line.append(timeNode)
  else if (shownVisitTime(cl)) line.append(el('span', { class: 'clean-time' }, shownVisitTime(cl)!))
  line.append(inHouse ? el('strong', { class: 'today-name' }, jobWord(cl)) : nameNode(cl.customer_id, cl.customer_name))
  const flag = !inHouse && cl.state !== 'done' ? stopFlag(cl) : null
  if (flag) line.append(flag)
  const priced = inHouse ? null : linePrice(cl, isOwnerToday())
  if (priced?.price) line.append(priced.price)
  main.append(line)
  const place = cl.address && !inHouse ? splitAccessWarning(cl.address) : null
  const where = inHouse ? '' : place ? fullAddress(cl, place.addr) : cl.postcode ?? ''
  if (where) {
    // Every day, past ones included (Liam, 2026-09-24): the address is the
    // line he looks for, and opening a map changes nothing on a finished day.
    const href = mapsHref(cl)
    main.append(
      href
        ? el('a', { href, target: '_blank', rel: 'noopener', class: 'today-address today-address-link ic ic-pin', 'aria-label': `Open ${where} in Maps` }, where)
        : el('span', { class: 'today-address ic ic-pin' }, where),
    )
  }
  // Folded, the street and the job on one quiet line; the full address opens with the stop.
  if (!inHouse) main.append(el('span', { class: 'stop-street' }, [place ? place.addr.split(',')[0]!.trim() : cl.postcode ?? '', jobWord(cl)].filter(Boolean).join(' · ')))
  if (place?.warn) main.append(el('span', { class: 'access-warn today-warn' }, place.warn))
  const clip = inHouse || when === 'past' || cl.state === 'done' ? null : noteClip(cl, row)
  if (clip) main.append(clip)

  const sub = el('span', { class: 'clean-badges' })
  // What the job is, in the diary's words (review 2026-09-24); on a house card the heading already says it.
  if (cl.kind && !inHouse) sub.append(el('span', { class: 'badge badge-kind' }, jobWord(cl)))
  // A copy of the day saved on this phone before prices were hidden may
  // still carry them: the switch wins over the copy.
  // The owner taps it to change it there and then, as in the Diary: just this visit, or this one and every one after
  // (Liam, 22 Oct 2026: "We changed her price should be easy to change here?").
  if (priced) sub.append(...priced.rest.map(wholePounds))
  else if (cl.amount_pence != null && !pricesHidden()) sub.append(...(isOwnerToday() ? [priceButton(cl, { className: 'badge', after: () => void reloadInPlace() }), ...extraBadges(cl.extras_json), ...askedBadges(cl.extras_asked_json)] : priceBadges(cl)).map(wholePounds))
  // Prices hidden: the extra work still shows, without its price (0087; Sonnet's extras review 8), and a helper's extras
  // still waiting for the owner's price (0091).
  else sub.append(...(cl.extras_json ? extraBadges(cl.extras_json, false) : []), ...askedBadges(cl.extras_asked_json))
  // A helper's parking sent to the owner, until the owner answers (0091).
  if (cl.parking_asked) sub.append(el('span', { class: 'badge badge-extra-asked' }, 'Parking sent to the owner'))
  const partTag = partBadge(cl)
  if (partTag) sub.append(partTag)
  // Credit set aside on it (0043, Liam Q1): "£5 from credit · £19 to collect", the same on every phone.
  // Credit still being worked out (Astra r4, finding 4): said instead, so nobody collects on a figure not ready.
  if (moneyPending(cl)) sub.append(el('span', { class: 'badge credit-badge credit-pending' }, 'Credit being worked out'))
  else if (fromCredit(cl) > 0 && cl.amount_pence != null && !pricesHidden()) sub.append(el('span', { class: 'badge credit-badge', title: creditWords(cl, money) ?? '' }, `${money(fromCredit(cl))} from credit · ${money(toCollect(cl))} to collect`))
  const agreement = cl.state === 'booked' ? todayVisitBadge(cl) : null
  if (agreement) sub.append(agreement)
  if (cl.state === 'done') {
    const pay = paymentWordsBadge(cl)
    pay.classList.add('stop-pay-tag')
    sub.append(pay)
  }
  if (cl.duration_minutes) sub.append(el('span', { class: 'badge badge-length' }, `${cl.duration_minutes}m`))
  main.append(sub)
  // Owed from Squeegee (0043): said at the door, so it can be collected (not to a worker who is not shown prices).
  const alsoOwes = pricesHidden() ? null : alsoOwesWords(cl, money)
  if (alsoOwes && cl.state === 'booked') main.append(el('p', { class: 'also-owes' }, alsoOwes))
  // Slid to finish, then Job done closed without saving (E2): finished, the payment still to record, so not done yet.
  if (cl.state === 'booked' && cl.finished_at && !cl.blocked_reason) main.append(el('p', { class: 'stop-to-record' }, `Finished ${clockOf(cl.finished_at)} · record payment to finish`))
  // Started on a day gone and never finished (E6): said, so the time can be put right.
  if (when === 'past' && cl.state === 'booked' && cl.started_at && !cl.finished_at && !cl.blocked_reason) main.append(el('p', { class: 'stop-to-record' }, `Started ${clockOf(cl.started_at)}, never finished`))
  // The note node is kept so a saved voice note updates THIS card in place —
  // no reload, so the rest of the day's screen never flickers mid-round.
  let noteBox: HTMLElement | null = null
  // A finished job's note is a Worker note in its own box under the times (0071); any other job's stays a line.
  const done = cl.state === 'done'
  const noteFor = (note: string): HTMLElement => (done ? workerNoteBox(cl, () => openNoteSheet()) : noteNode(note))
  const setNote = (note: string | null): void => {
    cl.note = note
    const next = note ? noteFor(note) : null
    if (noteBox && next) noteBox.replaceWith(next)
    else if (noteBox) noteBox.remove()
    else if (next && done) row.insertBefore(next, main.nextSibling)
    else if (next) main.insertBefore(next, sub.nextSibling)
    noteBox = next
  }
  if (cl.note && !done) {
    noteBox = noteNode(cl.note)
    main.append(noteBox)
  }
  // "Couldn't do it" (step 4) — the recorded outcome sits on the card as a
  // red line ("Couldn't do it · Not in · 14:20"), plus the note. Painted on
  // render; after a chooser save the WHOLE day re-renders from the updated
  // data (rerender) so the tally keeps up too (C2). The visit stays booked
  // and its tick stays live (they may come back later).
  const renderBlockedLine = (): void => {
    if (cl.state !== 'booked' || !cl.blocked_reason) return
    main.append(blockedLine(cl))
    // What happens next (docs/plans/rearrange/PLAN.md): the owner moves it to another day, from here whenever they like;
    // a worker can tell the customer someone will be in touch.
    const next = rearrangeButton(cl, row) ?? sorryButton(cl, row, setNote)
    if (next) main.append(next)
  }
  renderBlockedLine()
  // Job times (0020): "Started 09:12" while under way, "Arrived 09:12 · took
  // 34 min" once finished. A finished job's times can be put right, or added to one done without them, today or on a day
  // gone (0083; Astra: the next morning is when yesterday's gets put right).
  const times = jobTimesLine(cl)
  const mayTime = cl.state === 'done' && when !== 'future'
  if (times || mayTime) {
    const line = el('p', { class: 'job-times-line ic ic-clock' }, times ?? 'No times')
    if (mayTime) {
      const change = el('button', { type: 'button', class: 'job-times-change', 'aria-label': `${times ? 'Change times' : 'Add times'} for ${cl.customer_name}` }, times ? 'Change' : 'Add times')
      change.addEventListener('click', () => toggleChangeTimes(cl, row, rerender, main))
      line.append(' ', change)
    }
    main.append(line)
  }
  row.append(main)
  // The whole width of the card, clear of the done tick.
  if (cl.note && done) {
    noteBox = noteFor(cl.note)
    row.append(noteBox)
  }
  // From last time (0072): a note the owner chose to show once, on this next visit.
  const lastTime = done ? null : lastTimeBox(cl)
  if (lastTime) row.append(lastTime)

  // Access notes matter on the way there and at the door, not on a day gone. A house card shows them once. Once the job
  // is done they fold into one line, so its note is what shows (Liam: "make the access and notes collapsible"). On a
  // stop still to do they open from its clipped line.
  const notes = when === 'past' || inHouse ? [] : noteRows(cl)
  // Parking notes (0084, Extra features): "+ Add parking or times" for the owner, under the notes or on its own.
  const addNote = !done && when !== 'past' && !inHouse && parkingNotesOn() && isOwnerToday() ? addNoteLink(cl, () => void reloadInPlace()) : null
  if (notes.length && done) row.append(foldedAccessNotes(notes))
  else if (notes.length) {
    const box = el('div', { class: 'access-notes' }, el('strong', {}, 'Access & notes'))
    box.append(...notes)
    if (addNote) box.append(addNote)
    row.append(box)
  } else if (addNote) row.append(addNote)

  const openDone = (): void => {
    openBooking({
      customerId: cl.customer_id,
      cleanId: cl.id,
      completed: true,
      snapshot: { customer: { name: cl.customer_name, address: cl.address, postcode: cl.postcode, inside_price_pence: cl.customer_inside_price_pence ?? null }, clean: cl },
    })
  }
  // Which day a card belongs to decides what can be done on it (worker review, 2026-09-24): today's are started and
  // finished; a day gone can still be ticked done (a round that runs past midnight, or the cash written up next morning);
  // a day to come is for reading and getting there only (the server refuses Done on a day that has not come).
  const mayAct = cl.state === 'booked' && when !== 'future'
  // Focus mode has a big Done under Start job instead (F1).
  if (mayAct && !stop.focus) row.append(tickButton(cl, openDone, inHouse))
  // Photo (step 2): its button sits with the stop's other buttons; the strip of what this visit already carries, and the
  // label question for a new one, stay on the stop.
  const photosBox = el('div', { class: 'card-photos' })
  // Start / Finish (Liam, 2026-09-23): TODAY's jobs are started with one tap (the arrival time) and finished with a slide
  // (the time taken), then the usual Done panel records how it was paid. The tick stays on every stop, so a forgotten
  // Start never blocks logging the work.
  const startable = mayAct && when === 'today' && !cl.started_at && !cl.blocked_reason && !cl.finished_at
  const actions = el('div', { class: 'today-primary stop-actions' })
  let start: HTMLButtonElement | null = null
  if (startable) {
    start = el('button', { type: 'button', class: 'btn-primary job-start-btn ic ic-play' }, 'Start job') as HTMLButtonElement
    const button = start
    button.addEventListener('click', (e) => void startPressed(cl, row, button, e, rerender))
    actions.append(button)
  }
  if (mayAct && stop.focus) {
    const doneBig = el('button', { type: 'button', class: 'job-done-plain focus-done' }, 'Done')
    doneBig.addEventListener('click', openDone)
    actions.append(doneBig)
  }
  // Directions (Liam, 2026-09-23): the address is a maps link too, but a button is what gets tapped at the kerb. Contact
  // where Call was: Call and the ways to message them open under the stop (Liam, 21 Oct 2026; contact-tile.ts). A house
  // carries both once.
  const directions = inHouse || done ? null : mapsHref(cl)
  if (directions) actions.append(el('a', { href: directions, target: '_blank', rel: 'noopener', class: 'stop-square directions-link ic ic-navigate', 'aria-label': `Directions to ${cl.customer_name}` }, 'Directions'))
  const contact = inHouse || done ? null : contactButton({ name: cl.customer_name, phone: cl.phone, message_by: cl.message_by }, 'stop-square')
  if (contact) actions.append(contact.button)

  if (mayAct) {
    const subLine = [shownVisitTime(cl), where || null, jobWord(cl), cl.amount_pence != null && !pricesHidden() ? money(cl.amount_pence) : null].filter(Boolean).join(' · ')
    const sheet = stopSheet(row, inHouse ? `${jobWord(cl)} for ${cl.customer_name}` : cl.customer_name, inHouse ? `${cl.customer_name}: ${jobWord(cl)}` : cl.customer_name, subLine)
    actions.append(sheet.opener)
    const top = el('div', { class: 'stop-sheet-main' })
    if (start) {
      const fromSheet = el('button', { type: 'button', class: 'btn-primary job-start-sheet ic ic-play' }, 'Start job')
      const button = start
      fromSheet.addEventListener('click', () => { if (sheet.close()) button.click() })
      top.append(fromSheet)
    }
    const doneBtn = el('button', { type: 'button', class: start ? 'job-done-plain' : 'btn-primary ic ic-check' }, 'Done')
    doneBtn.addEventListener('click', () => { if (sheet.close()) openDone() })
    top.append(doneBtn)
    sheet.body.append(top)
    // Got going before pressing Start (Liam, 22 Oct 2026: "When I start the job and ... we're behind"): when it really
    // started, given here, so the job's time is the work's (job-time-ask.ts; 0083).
    if (start) {
      const link = el('button', { type: 'button', class: 'job-earlier-link' }, 'Started a while ago?')
      const earlier = el('div', { class: 'job-earlier' }, link)
      link.addEventListener('click', () => { if (sheet.held()) return; sheet.open(); toggleTimeAsk(cl, row, 'start', rerender, earlier) })
      sheet.body.append(earlier)
    }
    const tiles = el('div', { class: 'stop-sheet-tiles' })
    // Voice note: the row's version rides along so the server's version guard catches an edit made elsewhere between
    // render and send.
    // In Focus mode the voice note is one of the stop's own round buttons (F1); otherwise it waits in ⋯.
    const voice = el('button', { type: 'button', class: stop.focus ? 'stop-square voice-note-btn' : 'stop-tile voice-note-btn' })
    attachVoiceNote(voice, {
      clean: cl, // the live visit: its version follows every save on this card
      onSaved: (saved) => {
        cl.version = saved.version
        setNote(saved.note)
      },
    })
    if (stop.focus) actions.insertBefore(voice, sheet.opener)
    else tiles.append(voice)
    attachPhotos(photosBox, { clean: { id: cl.id }, buttonHost: tiles })
    // The phone's camera opens; the label question then comes up on the stop itself, so the sheet steps aside.
    tiles.addEventListener('click', (e) => { if ((e.target as Element).closest('.photo-btn')) sheet.close() })
    // Running late / early (worker plan): TODAY's stops only, not yet started, with a number a text can go to; never to a
    // customer who asked for no messages (contact.ts lateWay).
    if (when === 'today' && !cl.started_at && cleanPhone(cl.phone) && lateWay(cl) !== 'none') {
      const lateBtn = el('button', { type: 'button', class: 'stop-tile late-btn ic ic-clock' }, 'Running late')
      lateBtn.addEventListener('click', () => {
        if (sheet.held()) return
        sheet.open()
        const open = sheet.host.querySelector('.late-chooser')
        if (open) { open.remove(); return }
        sheet.host.replaceChildren()
        openLateChooser(cl, sheet.host, setNote)
      })
      tiles.append(lateBtn)
    }
    // Couldn't do it (step 4): its reasons, an optional note, Save / Back; nothing leaves the phone until Save, and Save
    // queues like Done when there is no signal. Opened again on a stop that has one, it edits it.
    const blockedBtn = el('button', { type: 'button', class: 'stop-tile blocked-btn ic ic-x' }, "Couldn't do it")
    blockedBtn.addEventListener('click', () => {
      if (sheet.held()) return
      sheet.open()
      const open = sheet.host.querySelector('.blocked-chooser')
      if (open) { open.remove(); return }
      sheet.host.replaceChildren()
      openBlockedChooser(cl, sheet.host, rerender)
    })
    tiles.append(blockedBtn)
    // Parking… (Liam, 27 Oct 2026, design B: "just need a way to get there"): the owner's goes onto the customer, a helper's
    // to the owner to check (src/parking-sheet.ts).
    const parkingBtn = el('button', { type: 'button', class: 'stop-tile ic ic-parking' }, 'Parking…')
    parkingBtn.addEventListener('click', () => {
      if (!sheet.close()) return
      void import('../parking-sheet').then(({ openParkingFor }) => openParkingFor({ id: cl.id, version: cl.version, customer_id: cl.customer_id, customer_name: cl.customer_name,
        parking: cl.parking_restrictions ?? null, customer_version: cl.customer_version ?? null }, !isOwnerToday(), () => void reloadInPlace()))
    })
    tiles.append(parkingBtn)
    // Pin the exact spot… (27 Oct 2026, the pin, design B's set): the owner's, at the door, straight onto the customer.
    if (isOwnerToday() && !inHouse) {
      const spotBtn = el('button', { type: 'button', class: 'stop-tile ic ic-pin' }, 'Pin the exact spot…')
      spotBtn.addEventListener('click', () => {
        if (!sheet.close()) return
        void import('../exact-spot').then(({ openSpotSheet }) => openSpotSheet({ id: cl.customer_id, name: cl.customer_name ?? 'them', link: cl.maps_link ?? null }, () => void reloadInPlace()))
      })
      tiles.append(spotBtn)
    }
    // The owner's: the price changed with them at the door (the Diary's price sheet, Liam, 22 Oct 2026), and their page.
    if (isOwnerToday() && !pricesHidden()) {
      const price = el('button', { type: 'button', class: 'stop-tile ic ic-edit' }, 'Change price')
      price.addEventListener('click', () => { if (sheet.close()) openVisitPrice(cl, () => void reloadInPlace()) })
      tiles.append(price)
    }
    if (isOwnerToday()) tiles.append(el('a', { href: `#/customer/${cl.customer_id}`, class: 'stop-tile ic ic-person' }, 'Their page'))
    sheet.body.append(tiles)
    if (!inHouse) {
      const ways = el('div', { class: 'stop-sheet-ways' })
      if (directions) ways.append(el('a', { href: directions, target: '_blank', rel: 'noopener', class: 'btn-secondary ic ic-navigate' }, 'Directions'))
      const reach = contactButton({ name: cl.customer_name, phone: cl.phone, message_by: cl.message_by }, 'btn-secondary')
      if (reach) ways.append(reach.button)
      if (ways.childElementCount) sheet.body.append(ways)
      if (reach?.choices) sheet.body.append(reach.choices)
    }
  }
  if (actions.childElementCount) row.append(actions)
  if (contact?.choices) row.append(contact.choices)

  // A logged job's payment can be put right from the phone (worker review
  // 2026-09-24: Bank tapped for Cash, £60 typed for £6, or a customer who
  // paid later). The same panel, opening on the answer already saved; the
  // server lets a worker change the payment of their own done visit.
  // A note is what gets added after a job far more often than a payment is put right (Liam, 13 Oct 2026: "after job done
  // we might think of a note but we wouldnt change payment as often after the fact"): Add a note is the wide button, and
  // Payment a tile with the others. The note opens on its own (booking.ts openNote) and saves alone. A finished stop
  // folds to one green line; tapped, it opens on these (Astra: every after-done action keeps a way in).
  let noteWide: HTMLElement | null = null
  const openNoteSheet = (): void => openBooking({ customerId: cl.customer_id, cleanId: cl.id, completed: true, openNote: true,
    snapshot: { customer: { name: cl.customer_name, address: cl.address, postcode: cl.postcode, inside_price_pence: cl.customer_inside_price_pence ?? null }, clean: cl },
    // Shown on the card the moment it saves (Liam: "The message we add should instanly appear").
    onNoteSaved: (note, version) => {
      if (version !== null) cl.version = version
      // Who and when, as the server has just recorded them (0071), until the day is read again.
      cl.note_at = nowStamps().stored
      cl.note_by_name = getMe()?.workerName?.trim() || getMe()?.email || null
      setNote(note)
      if (noteWide) (noteWide.parentElement as HTMLElement).hidden = !!note
    } })
  if (done && when !== 'future') {
    noteWide = el('button', { type: 'button', class: 'today-change-pay today-add-note ic ic-notes' }, 'Add a note')
    noteWide.addEventListener('click', openNoteSheet)
    const wide = el('div', { class: 'today-primary today-done-note' }, noteWide)
    wide.hidden = !!cl.note
    row.append(wide)
  }
  if (done) {
    const tools = el('div', { class: 'today-tools' })
    // Payment where Directions was: the Job done sheet on its payment (Change payment), Part done included.
    if (when !== 'future') {
      const pay = el('button', { type: 'button', class: 'today-tool today-pay-tool ic ic-edit', 'aria-label': `Change payment for ${cl.customer_name}` }, 'Payment')
      pay.addEventListener('click', openDone)
      tools.append(pay)
    }
    const reach = inHouse ? null : contactButton({ name: cl.customer_name, phone: cl.phone, message_by: cl.message_by }, 'today-tool')
    if (reach) tools.append(reach.button)
    if (when !== 'future') {
      const voice = el('button', { type: 'button', class: 'today-tool voice-note-btn' })
      attachVoiceNote(voice, { clean: cl, onSaved: (saved) => { cl.version = saved.version; setNote(saved.note) } })
      tools.append(voice)
    }
    attachPhotos(photosBox, when === 'future' ? { clean: { id: cl.id } } : { clean: { id: cl.id }, buttonHost: tools })
    if (tools.childElementCount) row.append(tools)
    if (reach?.choices) row.append(reach.choices)
  } else if (!mayAct) attachPhotos(photosBox, { clean: { id: cl.id } })
  row.append(photosBox)
  return row
}

// ---- the job under way ---------------------------------------------------------

// A started job (Liam, 2026-09-24): "everything gone except Finish job" and
// round buttons for what a worker does mid-job. They are already at the door,
// so the time slot, the address, the price and the notes box go; the notes
// sit behind Notes, and Call, Directions, Couldn't do it and an undo for a
// Start tapped by mistake sit behind More.
function renderLiveCard(cl: TodayClean, row: HTMLElement, rerender: () => void, next?: TodayClean, stop: StopPlace = { n: null, open: true }): HTMLElement {
  row.classList.add('today-clean-live', 'today-live', 'is-open')
  if (stop.focus) row.classList.add('is-focus')
  if (stop.n !== null) row.append(stopNum(stop.n, false))
  const head = el('div', { class: 'live-head' }, el('strong', { class: 'today-name' }, cl.customer_name))
  const since = jobTimesLine(cl)
  // Tap "Started 1:28pm" to put the start right (Liam, 26 Oct 2026: "I started this job later than i actually did but theres no
  // where to say what time i started", then "Yes make it tappable"): the same as More > Change start time.
  if (since) {
    const sinceBtn = el('button', { type: 'button', class: 'live-since live-since-btn', 'aria-label': `${since}: change the start time` },
      el('span', { class: 'live-dot', 'aria-hidden': 'true' }), since, el('span', { class: 'live-since-pencil', 'aria-hidden': 'true' }, '✎'))
    sinceBtn.addEventListener('click', () => toggleChangeTimes(cl, row, rerender, head))
    head.append(sinceBtn)
  }
  row.append(head)
  // The address stays while the job is under way (Liam, 24 Oct 2026: "I have forgotten house numbers"), and the gate code
  // and parking with it on one clipped line (2026-09-24: Start had hidden them behind Notes when they were needed); a tap
  // on the line opens Notes.
  const place = cl.address ? splitAccessWarning(cl.address) : null
  const where = place ? fullAddress(cl, place.addr) : cl.postcode ?? ''
  if (where) {
    const href = mapsHref(cl)
    row.append(href
      ? el('a', { href, target: '_blank', rel: 'noopener', class: 'today-address today-address-link live-address ic ic-pin', 'aria-label': `Open ${where} in Maps` }, where)
      : el('span', { class: 'today-address live-address ic ic-pin' }, where))
  }
  const clipSlot = el('div', { class: 'live-clip' })
  row.append(clipSlot)
  // From last time (0072) stays at the door while the job is under way: on the clipped line, whole in Notes.

  const openDone = (): void => {
    openBooking({
      customerId: cl.customer_id,
      cleanId: cl.id,
      completed: true,
      snapshot: { customer: { name: cl.customer_name, address: cl.address, postcode: cl.postcode, inside_price_pence: cl.customer_inside_price_pence ?? null }, clean: cl },
    })
  }
  // Slide to finish (Liam, 2026-09-24): cannot happen by a brush of the
  // screen, and reaching the end feels like the job is done.
  const openDoneAfterSlide = (): void => {
    openBooking({
      customerId: cl.customer_id,
      cleanId: cl.id,
      completed: true,
      snapshot: { customer: { name: cl.customer_name, address: cl.address, postcode: cl.postcode, inside_price_pence: cl.customer_inside_price_pence ?? null }, clean: cl },
      notFinished: () => clearFinish(cl, rerender),
    })
  }
  // The iPhone noticed them leave a while ago (timer nudges, 0083): the question takes the slide's place until answered,
  // or put aside with Still working (a trip to the van past the edge of the street). Otherwise the slide finishes now,
  // and "Finished a while ago?" under it gives the time it really finished.
  const left = noticedAt(cl.id, 'left', cl.date)
  const finishIt = (at: Date, how: JobTimeHow): void => setJobTime(cl, 'finished_at', rerender, openDoneAfterSlide, at, how)
  const slideArea = el('div', { class: 'live-finish' })
  const drawSlide = (): void => {
    const link = el('button', { type: 'button', class: 'job-earlier-link finish-earlier-link' }, 'Finished a while ago?')
    const under = el('div', { class: 'job-earlier' }, link)
    link.addEventListener('click', () => toggleTimeAsk(cl, row, 'finish', rerender, under, { then: openDoneAfterSlide, asSheet: true }))
    slideArea.replaceChildren(slideToConfirm({ label: 'Slide to finish', doneLabel: 'Finished', onConfirm: () => finishIt(new Date(), 'tap') }), under)
  }
  // Outsides done, doing insides (0085; Liam, 22 Oct 2026, Karyn's job: "It took us thirty two minutes to do all the
  // outsides ... then I spent half an hour doing four windows inside"): one tap when going inside, so each is timed.
  // Its own place above the slide: drawSlide replaces the slide's box, which used to take the button with it (Astra).
  if (!cl.outsides_done_at) {
    const outs = el('button', { type: 'button', class: 'live-outsides' }, 'Outsides done, doing insides')
    outs.addEventListener('click', () => setOutsidesDone(cl, new Date(), rerender))
    row.append(el('div', { class: 'live-outsides-row' }, outs))
  }
  if (left && !stillWorking.has(`${cl.id}:${left.getTime()}`)) {
    const asked = timeChooser({
      kind: 'finish', day: cl.date, guess: finishGuess(cl, left), offerNow: true, notBefore: storedDate(cl.started_at), backLabel: 'Still working',
      onPick: ({ at, how }) => finishIt(at, how),
      onBack: () => { stillWorking.add(`${cl.id}:${left.getTime()}`); drawSlide() },
    })
    asked.classList.add('time-chooser-asked')
    slideArea.append(asked)
  } else drawSlide()
  row.append(slideArea)

  const tools = el('div', { class: 'live-tools' })
  const voice = el('button', { type: 'button', class: 'voice-note-btn' })
  attachVoiceNote(voice, {
    clean: cl, // the live visit: its version follows every save on this card
    onSaved: (saved) => {
      cl.version = saved.version
      cl.note = saved.note
      paintNotes()
      toast('Voice note added to the notes')
    },
  })
  tools.append(voice)

  const notesBtn = el('button', { type: 'button', class: 'live-notes-btn ic ic-notes', 'aria-expanded': 'false' }, 'Notes')
  const notesBox = el('div', { class: 'live-notes', hidden: '' })
  const paintNotes = (): void => {
    clear(notesBox)
    const access = noteRows(cl)
    const warn = cl.address ? splitAccessWarning(cl.address).warn : null
    if (warn) notesBox.append(el('p', { class: 'access-warn today-warn' }, warn))
    if (access.length) {
      notesBox.append(el('strong', {}, 'Access & notes'))
      notesBox.append(...access)
    }
    // From last time, whole, behind the clipped line (it used to sit on the card).
    const last = lastTimeBox(cl)
    if (last) notesBox.append(last)
    if (cl.note) notesBox.append(noteNode(cl.note))
    if (!access.length && !cl.note && !warn && !last) notesBox.append(el('p', { class: 'muted' }, 'No notes for this job yet.'))
    // Parking notes (0084): the same link while the job is under way.
    const add = parkingNotesOn() && isOwnerToday() ? addNoteLink(cl, () => void reloadInPlace()) : null
    if (add) notesBox.append(add)
  }
  paintNotes()
  notesBtn.addEventListener('click', () => {
    notesBox.hidden = !notesBox.hidden
    notesBtn.setAttribute('aria-expanded', String(!notesBox.hidden))
    notesBtn.classList.toggle('live-on', !notesBox.hidden)
  })
  const clip = noteClip(cl, row, () => notesBtn.click())
  if (clip) clipSlot.append(clip)
  else clipSlot.remove()

  const more = el('details', { class: 'live-more' })
  const summary = el('summary', { class: 'ic ic-more', 'aria-label': 'More for this job' }, 'More')
  const list = el('div', { class: 'live-more-list', role: 'menu' })
  const close = (): void => {
    more.open = false
  }
  // Call, then the ways to message them, as flat lines (contact-tile.ts): the job's contact stays while it runs.
  const tel = telHref(cl.phone)
  if (tel) list.append(el('a', { href: tel, class: 'ic ic-phone', role: 'menuitem' }, 'Call'))
  list.append(...contactLines({ name: cl.customer_name, phone: cl.phone, message_by: cl.message_by }, 'live-more-chat', { call: false, icons: true, onOpen: close }))
  const maps = mapsHref(cl)
  if (maps) list.append(el('a', { href: maps, target: '_blank', rel: 'noopener', class: 'ic ic-navigate', role: 'menuitem' }, 'Directions'))
  const blocked = el('button', { type: 'button', class: 'live-more-danger ic ic-x', role: 'menuitem' }, "Couldn't do it")
  blocked.addEventListener('click', () => {
    close()
    if (!row.querySelector('.blocked-chooser')) openBlockedChooser(cl, row, rerender)
  })
  const undo = el('button', { type: 'button', class: 'ic ic-undo', role: 'menuitem' }, 'Started by mistake')
  undo.addEventListener('click', () => {
    close()
    clearStart(cl, rerender)
  })
  // Outsides done pressed too soon (0085): taken off.
  const outsNot = el('button', { type: 'button', class: 'ic ic-undo', role: 'menuitem' }, 'Outsides not done yet')
  outsNot.addEventListener('click', () => { close(); setOutsidesDone(cl, null, rerender) })
  // Pressed Start late, or the time is wrong (0083): put right while the job runs.
  const changeStart = el('button', { type: 'button', class: 'ic ic-clock', role: 'menuitem' }, 'Change start time')
  changeStart.addEventListener('click', () => {
    close()
    toggleChangeTimes(cl, row, rerender, head)
  })
  // Running over here means the NEXT customer is waiting (Liam, 2026-09-24:
  // "an easy way if we're gonna be late to notify the customer"). Opens that
  // job's own Running late text, ready to send from this phone's Messages.
  if (next) {
    const late = el('button', { type: 'button', class: 'ic ic-clock', role: 'menuitem' }, `Running late for ${firstName(next.customer_name)}`)
    late.addEventListener('click', () => {
      close()
      let card = document.querySelector<HTMLElement>(`#today-day .today-clean[data-clean-id="${next.id}"]`)
      // Focus mode shows one stop: it moves to theirs first.
      if (!card && focusMode && lastDrawn) {
        if (focusHeld()) return
        focusStopId = next.id; focusDir = 1
        renderDay(lastDrawn.data, lastDrawn.cachedAt)
        card = document.querySelector<HTMLElement>(`#today-day .today-clean[data-clean-id="${next.id}"]`)
      }
      if (!card) return
      card.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'center' })
      if (!card.querySelector('.late-chooser')) card.querySelector<HTMLButtonElement>('.late-btn')?.click()
    })
    list.append(late)
  }
  // The price changed with them at the door: the Diary's price sheet, the owner's only (Liam, 22 Oct 2026).
  if (isOwnerToday() && !pricesHidden()) {
    const price = el('button', { type: 'button', class: 'ic ic-edit', role: 'menuitem' }, 'Change price')
    price.addEventListener('click', () => { close(); openVisitPrice(cl, () => void reloadInPlace()) })
    list.append(price)
  }
  list.append(blocked, el('hr', { class: 'row-menu-rule' }), changeStart, ...(cl.outsides_done_at ? [outsNot] : []), undo)
  for (const a of Array.from(list.querySelectorAll('a'))) a.addEventListener('click', close)
  more.append(summary, list)
  bindLiveMenuDismiss()

  // The Photo button joins the round buttons; the strip of photos already on
  // the visit hangs under them.
  const photosBox = el('div', { class: 'card-photos' })
  attachPhotos(photosBox, { clean: { id: cl.id }, buttonHost: tools })
  tools.append(notesBtn, more)
  row.append(tools, notesBox, photosBox)
  return row
}

let liveMenuDismissBound = false
// The More menu floats over the card, so a tap anywhere else or Escape shuts
// it (the diary's ⋯ menus behave the same way).
function bindLiveMenuDismiss(): void {
  if (liveMenuDismissBound) return
  liveMenuDismissBound = true
  const shut = (keep: Node | null): void => {
    for (const m of Array.from(document.querySelectorAll<HTMLDetailsElement>('details.live-more[open]'))) {
      if (!keep || !m.contains(keep)) m.open = false
    }
  }
  document.addEventListener('click', (e) => shut(e.target as Node))
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') shut(null)
  })
  window.addEventListener('hashchange', () => shut(null))
}

// "Not finished yet", from the panel that Slide to finish opens: the finish
// time comes off and the job is back on the lemon card.
function clearFinish(cl: TodayClean, rerender: () => void): void {
  const version = cl.version
  const was = cl.finished_at ?? null
  cl.finished_at = null
  rerender()
  void mutate<TodayClean>({
    label: `Undo finish: ${cl.customer_name}`,
    method: 'PATCH',
    path: `/api/cleans/${cl.id}`,
    body: { version, finished_at: null },
    onConflict: () => void load(),
  }).then((ack) => {
    if (ack.ok) {
      const saved = ack.row as TodayClean | undefined
      if (saved) cl.version = saved.version
      toast(`${cl.customer_name} is still under way`)
    } else if (ack.status === 0) {
      toast('No signal. Saved on this phone to send later.')
    } else if (ack.status >= 400 && ack.status < 500 && ack.status !== 409) {
      cl.finished_at = was
      rerender()
      window.alert(ack.error ?? 'rejected')
    }
  })
}

// "Started by mistake": the start comes off and the card goes back to how it
// was. The same PATCH the Undo toast sends.
function clearStart(cl: TodayClean, rerender: () => void): void {
  const version = cl.version
  const was = cl.started_at ?? null
  cl.started_at = null
  rerender()
  void mutate<TodayClean>({
    label: `Undo start: ${cl.customer_name}`,
    method: 'PATCH',
    path: `/api/cleans/${cl.id}`,
    body: { version, started_at: null },
    onConflict: () => void load(),
  }).then((ack) => {
    if (ack.ok) {
      const saved = ack.row as TodayClean | undefined
      if (saved) cl.version = saved.version
      toast(`Took the start off ${cl.customer_name}`)
    } else if (ack.status === 0) {
      toast('No signal. Saved on this phone to send later.')
    } else if (ack.status >= 400 && ack.status < 500 && ack.status !== 409) {
      cl.started_at = was
      rerender()
      window.alert(ack.error ?? 'rejected')
    }
  })
}

// ---- Start job: the hand-over into the live card ---------------------------------

// Liam, 2026-09-24: "a nice satisfying animation into this from start job".
// About half a second, in two halves either side of the redraw:
//   1. on the tap, the button turns slate and everything the live card drops
//      (address, labels, notes, the other buttons) fades up and away;
//   2. the day is drawn again, and the new card grows or shrinks from the old
//      card's height while lemon spreads out from where the finger was, the
//      tick pops in on Finish job and the round buttons rise one by one.
// The time recorded is the moment of the tap, not the end of the animation.
// A phone set to reduce motion gets the change at once.
interface PendingReveal {
  id: number
  x: number
  y: number
  fromHeight: number
  at: number
}
let pendingReveal: PendingReveal | null = null

function reducedMotion(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches
}

function startWithReveal(cl: TodayClean, row: HTMLElement, button: HTMLButtonElement, e: MouseEvent, rerender: () => void, at: Date = new Date()): void {
  if (button.disabled) return
  button.disabled = true
  if (reducedMotion()) {
    setJobTime(cl, 'started_at', rerender, undefined, at, 'tap')
    return
  }
  const box = row.getBoundingClientRect()
  // A keyboard press has no finger: spread from the middle of the button.
  const b = button.getBoundingClientRect()
  const x = e.clientX || e.clientY ? e.clientX : b.left + b.width / 2
  const y = e.clientX || e.clientY ? e.clientY : b.top + b.height / 2
  pendingReveal = { id: cl.id, x: x - box.left, y: y - box.top, fromHeight: box.height, at: Date.now() }
  if (typeof navigator.vibrate === 'function') navigator.vibrate(12)
  setJobTime(cl, 'started_at', rerender, undefined, at, 'tap')
}

// ---- when the job really started or finished (0083) ------------------------------------------------------------------

// Start job pressed. The iPhone noticed them arrive here a few minutes ago (timer nudges): asked when it really started,
// the box holding the phone's time and a minute to get going, with Starting now beside it. Otherwise it starts now.
async function startPressed(cl: TodayClean, row: HTMLElement, button: HTMLButtonElement, e: MouseEvent, rerender: () => void): Promise<void> {
  if (button.disabled) return
  const at = new Date()
  // Read again: the phone may have noticed them since the day was drawn. Nothing to wait for in a browser.
  await readNoticed(cl.date)
  if (!noticedAt(cl.id, 'arrived', cl.date, at.getTime())) { startWithReveal(cl, row, button, e, rerender, at); return }
  toggleTimeAsk(cl, row, 'start', rerender, button.closest<HTMLElement>('.today-primary') ?? button, { toggle: false, offerNow: true })
}

// Roundly's best guess at when it really started or finished (job-time-ask.ts): the phone's noticed arrival or leaving,
// else the job's booked time (a start) or its start and booked length (a finish), else now.
function startGuess(cl: TodayClean): TimeGuess {
  const before = finishedBefore(cl)
  return guessStart({ day: cl.date, noticed: noticedAt(cl.id, 'arrived', cl.date), booked: visibleTime(cl), finishedBefore: before?.at ?? null, now: new Date() })
}
function finishGuess(cl: TodayClean, left: Date | null = noticedAt(cl.id, 'left', cl.date)): TimeGuess {
  return guessFinish({ noticed: left, startedAt: storedDate(cl.started_at), minutes: cl.duration_minutes ?? null, now: new Date() })
}

// When the job before this one finished, so a start given earlier than that is said (Astra's overlap hold): the latest
// finish on the person's day, or the latest no later than `upTo`.
let drawnVisits: TodayClean[] = []
function finishedBefore(cl: TodayClean, upTo: string | null = null): { at: Date; words: string } | null {
  let best: TodayClean | null = null
  for (const v of drawnVisits) {
    // Only the same person's day: Everyone holds every worker's jobs (Astra).
    if ((v.phone_of ?? '') !== (cl.phone_of ?? '')) continue
    if (v.id === cl.id || !v.finished_at || (upTo && v.finished_at > upTo)) continue
    if (!best || v.finished_at > best.finished_at!) best = v
  }
  return best ? { at: storedDate(best.finished_at)!, words: `That's before you finished ${firstName(best.customer_name)} at ${clockOf(best.finished_at!)}.` } : null
}

const storedDate = (s: string | null | undefined): Date | null => (s ? new Date(`${s.replace(' ', 'T')}Z`) : null)
const sentForm = (s: string | null | undefined): string | null => (s ? `${s.replace(' ', 'T')}Z` : null)

// "Still working" pressed on the leaving question: that noticed leaving is not asked about again on this page.
const stillWorking = new Set<string>()

// The question in the card, under `after`; pressed again, it closes (`toggle`). `then` follows a finish (the Job done
// sheet), as after the slide; `offerNow` adds Starting now / Finishing now.
function toggleTimeAsk(cl: TodayClean, row: HTMLElement, kind: 'start' | 'finish', rerender: () => void, after: HTMLElement,
  { then, toggle = true, offerNow = false, asSheet = false }: { then?: () => void; toggle?: boolean; offerNow?: boolean; asSheet?: boolean } = {}): void {
  // "Finished a while ago?" (Liam picked B, 26 Oct 2026, https://claude.ai/artifact/NinRbbtA3w5jNDDiRNTxYU): a sheet over
  // the day, the wheel already on the guess and one button, "Finished at 3:08pm". The other questions stay in the card
  // (Started a while ago? is already inside the stop's own sheet), with the same wheel.
  if (asSheet) {
    const words = kind === 'start' ? 'start' : 'finish'
    // The day read again while it is open waits until it closes, so its Save never goes with an old copy of the job (Sol).
    const sheet = bottomSheet(`When did you ${words} ${cl.customer_name}?`, () => { if (reloadWaiting) { reloadWaiting = false; void load() } })
    sheet.dialog.classList.add('time-sheet')
    sheet.head.append(el('h3', { class: 'diary-sheet-title' }, `When did you ${words} ${cl.customer_name}?`))
    const asked = timeChooser({
      kind, day: cl.date, guess: kind === 'start' ? startGuess(cl) : finishGuess(cl), offerNow,
      notBefore: kind === 'finish' ? storedDate(cl.started_at) : null,
      warnBefore: kind === 'start' ? finishedBefore(cl) : null,
      onPick: ({ at, how }) => { sheet.close(); setJobTime(cl, kind === 'start' ? 'started_at' : 'finished_at', rerender, then, at, how) },
      onBack: () => sheet.close(),
    })
    sheet.body.append(asked)
    return
  }
  const open = row.querySelector('.time-chooser:not(.time-chooser-asked):not(.change-times)')
  // While the question is open its own link ("Started a while ago?", "Finished a while ago?") steps aside.
  const links = Array.from(row.querySelectorAll<HTMLElement>('.job-earlier'))
  const close = (box: Element | null): void => { box?.remove(); for (const l of links) l.hidden = false }
  close(open)
  if (open && toggle) return
  const chooser = timeChooser({
    kind, day: cl.date, guess: kind === 'start' ? startGuess(cl) : finishGuess(cl), offerNow,
    notBefore: kind === 'finish' ? storedDate(cl.started_at) : null,
    warnBefore: kind === 'start' ? finishedBefore(cl) : null,
    onPick: ({ at, how }) => { close(chooser); setJobTime(cl, kind === 'start' ? 'started_at' : 'finished_at', rerender, then, at, how) },
    onBack: () => close(chooser),
  })
  after.after(chooser)
  for (const l of links) l.hidden = true
  chooser.scrollIntoView?.({ block: 'nearest', behavior: reducedMotion() ? 'auto' : 'smooth' })
  // The link pressed steps aside, so the keyboard and a screen reader go to the question (never into the time box, which
  // would open the phone's wheel by itself).
  chooser.setAttribute('tabindex', '-1')
  chooser.focus({ preventScroll: true })
}

// Change times on a finished job, or the start of one under way, under `after`.
function toggleChangeTimes(cl: TodayClean, row: HTMLElement, rerender: () => void, after: HTMLElement): void {
  const open = row.querySelector('.change-times')
  if (open) { open.remove(); return }
  const running = cl.state === 'booked' && !cl.finished_at
  const chooser = changeTimesChooser({
    day: cl.date,
    started_at: cl.started_at ?? null,
    started_how: cl.started_how ?? null,
    ...(running ? {} : { finished_at: cl.finished_at ?? null, finished_how: cl.finished_how ?? null }),
    // The phone's last arrival and leaving here, offered to fill in, even with a leaving after the arrival.
    noticed: { arrived: noticedAt(cl.id, 'arrived', cl.date, Date.now(), true), left: running ? null : noticedAt(cl.id, 'left', cl.date, Date.now(), true) },
    warnBefore: finishedBefore(cl, cl.started_at ?? null),
    onSave: (change) => { chooser.remove(); saveTimes(cl, change, rerender) },
    onBack: () => chooser.remove(),
  })
  after.after(chooser)
  chooser.scrollIntoView?.({ block: 'nearest', behavior: reducedMotion() ? 'auto' : 'smooth' })
}

// Outsides done (0085), or taken off (null): saved at once, with Undo; with no signal it waits with its real time.
function setOutsidesDone(cl: TodayClean, at: Date | null, rerender: () => void): void {
  const version = cl.version
  const was = cl.outsides_done_at ?? null
  const { iso, stored } = nowStamps(at ?? new Date())
  cl.outsides_done_at = at ? stored : null
  rerender()
  void mutate<TodayClean>({
    label: `${at ? 'Outsides done' : 'Outsides not done'}: ${cl.customer_name}`,
    method: 'PATCH',
    path: `/api/cleans/${cl.id}`,
    body: { version, outsides_done_at: at ? iso : null },
    onConflict: () => void load(),
  }).then((ack) => {
    if (ack.ok) {
      const saved = ack.row as TodayClean | undefined
      if (saved) { cl.version = saved.version; cl.outsides_done_at = saved.outsides_done_at ?? null }
    } else if (ack.status >= 400 && ack.status < 500 && ack.status !== 409) {
      cl.outsides_done_at = was
      rerender()
      window.alert(ack.error ?? 'rejected')
      return
    } else if (ack.status === 0) {
      toast('No signal. Saved on this phone to send later.')
    }
    if (at && (ack.ok || ack.status === 0)) {
      undoPatch({ text: `Outsides done for ${cl.customer_name} at ${clockOf(stored)}`, label: `Undo outsides done: ${cl.customer_name}`, path: `/api/cleans/${cl.id}`, ack, version,
        body: { outsides_done_at: null }, after: () => void load() })
    }
  })
}

// One save for the times put right, with Undo putting back exactly what changed.
function saveTimes(cl: TodayClean, change: ChangeTimesSave, rerender: () => void): void {
  const version = cl.version
  const was = { started_at: cl.started_at ?? null, started_how: cl.started_how ?? null, finished_at: cl.finished_at ?? null, finished_how: cl.finished_how ?? null }
  const back: Record<string, unknown> = {}
  if (change.started_at !== undefined) { back.started_at = sentForm(was.started_at); back.started_how = was.started_how }
  if (change.started_how !== undefined) back.started_how = was.started_how
  if (change.finished_at !== undefined) { back.finished_at = sentForm(was.finished_at); back.finished_how = was.finished_how }
  if (change.finished_how !== undefined) back.finished_how = was.finished_how
  const show = (): void => {
    if (change.started_at !== undefined) cl.started_at = nowStamps(new Date(change.started_at)).stored
    if (change.finished_at !== undefined) cl.finished_at = nowStamps(new Date(change.finished_at)).stored
    if (change.started_at !== undefined || change.started_how !== undefined) cl.started_how = change.started_how ?? null
    if (change.finished_at !== undefined || change.finished_how !== undefined) cl.finished_how = change.finished_how ?? null
  }
  show()
  rerender()
  void mutate<TodayClean>({
    label: `Times: ${cl.customer_name}`,
    method: 'PATCH',
    path: `/api/cleans/${cl.id}`,
    body: { version, ...change },
    onConflict: () => void load(),
  }).then((ack) => {
    if (ack.ok) {
      const saved = ack.row as TodayClean | undefined
      if (saved) {
        cl.version = saved.version
        cl.started_at = saved.started_at ?? null
        cl.finished_at = saved.finished_at ?? null
        cl.started_how = saved.started_how ?? null
        cl.finished_how = saved.finished_how ?? null
      }
    } else if (ack.status === 403) {
      toast('This job has been given to someone else')
      void load()
      return
    } else if (ack.status >= 400 && ack.status < 500 && ack.status !== 409) {
      Object.assign(cl, was)
      rerender()
      window.alert(ack.error ?? 'rejected')
      return
    } else if (ack.status === 0) {
      toast('No signal. The times are saved on this phone to send later.')
    }
    if (ack.ok || ack.status === 0) {
      undoPatch({ text: `Times changed for ${cl.customer_name}`, label: `Undo times: ${cl.customer_name}`, path: `/api/cleans/${cl.id}`, ack, version,
        body: back, after: () => void load() })
    }
  })
}

function playPendingReveal(container: HTMLElement): void {
  const r = pendingReveal
  if (!r) return
  pendingReveal = null
  if (Date.now() - r.at > 3000) return
  const card = container.querySelector<HTMLElement>(`.today-live[data-clean-id="${r.id}"]`)
  if (!card || typeof card.animate !== 'function') return
  const to = card.offsetHeight
  const tall = Math.max(to, r.fromHeight)
  const w = card.offsetWidth
  const radius = Math.ceil(Math.max(Math.hypot(r.x, r.y), Math.hypot(w - r.x, r.y), Math.hypot(r.x, tall - r.y), Math.hypot(w - r.x, tall - r.y)))
  const wash = el('span', { class: 'live-wash', 'aria-hidden': 'true' })
  card.prepend(wash)
  card.classList.add('live-enter', 'live-revealing')
  card.style.overflow = 'hidden'
  const ease = 'cubic-bezier(.2,.8,.2,1)'
  wash.animate(
    [{ clipPath: `circle(0px at ${r.x}px ${r.y}px)` }, { clipPath: `circle(${radius}px at ${r.x}px ${r.y}px)` }],
    { duration: 280, easing: ease, fill: 'forwards' },
  )
  const grow = card.animate([{ height: `${r.fromHeight}px` }, { height: `${to}px` }], { duration: 250, easing: ease })
  const done = (): void => {
    card.style.overflow = ''
    card.classList.remove('live-revealing', 'live-enter')
    wash.remove()
  }
  void grow.finished.then(() => window.setTimeout(done, 40), done)
}

// ---- Start / Finish -------------------------------------------------------------

// Records the moment of the tap ON THE PHONE and sends it (a tap in a dead
// zone keeps its real time and goes when signal returns, like every van
// write). The card changes at once; Undo puts it back. Finish then opens the
// Done panel so the payment is recorded as it always was.
function setJobTime(
  cl: TodayClean,
  field: 'started_at' | 'finished_at',
  rerender: () => void,
  then?: () => void,
  at?: Date,
  // How the time was got (0083): tapped now, the phone's noticed time, given, rough, not sure.
  how: JobTimeHow = 'tap',
): void {
  const { iso, stored } = nowStamps(at)
  const version = cl.version
  const howField = field === 'started_at' ? 'started_how' : 'finished_how'
  cl[field] = stored
  cl[howField] = how
  rerender()
  const what = field === 'started_at' ? 'Started' : 'Finished'
  void mutate<TodayClean>({
    label: `${what}: ${cl.customer_name}`,
    method: 'PATCH',
    path: `/api/cleans/${cl.id}`,
    body: { version, [field]: iso, [howField]: how },
    // Edited elsewhere first: nothing was written; show the day as it is.
    onConflict: () => void load(),
  }).then((ack) => {
    if (ack.ok) {
      const saved = ack.row as TodayClean | undefined
      if (saved) {
        cl.version = saved.version
        cl.started_at = saved.started_at ?? cl.started_at
        cl.finished_at = saved.finished_at ?? cl.finished_at
        cl.started_how = saved.started_how ?? null
        cl.finished_how = saved.finished_how ?? null
      }
    } else if (ack.status === 403) {
      // Given to someone else while it was on this phone (review 2026-09-24):
      // say so and take it off the day.
      toast('This job has been given to someone else')
      void load()
      return
    } else if (ack.status >= 400 && ack.status < 500 && ack.status !== 409) {
      cl[field] = null
      cl[howField] = null
      rerender()
      window.alert(ack.error ?? 'rejected')
      return
    } else if (ack.status === 0) {
      toast(`No signal. ${what} ${clockOf(stored)} is saved on this phone to send later.`)
    }
    if (ack.ok || ack.status === 0) {
      undoPatch({
        text: how === 'unsure' ? `${what} ${cl.customer_name}, time not known` : `${what} ${cl.customer_name} at ${how === 'rough' ? 'about ' : ''}${clockOf(stored)}`,
        label: `Undo ${what.toLowerCase()}: ${cl.customer_name}`,
        path: `/api/cleans/${cl.id}`,
        ack,
        version,
        body: { [field]: null },
        after: () => void load(),
      })
      then?.()
    }
  })
}

// ---- the "Couldn't do it" chooser ---------------------------------------------

// Five reasons as van-sized buttons (44px), an optional one-line note, Save /
// Back. Opens pre-filled with what the card already carries, so re-opening a
// saved outcome edits it rather than starting over. blocked_at is the
// server's stamp — the phone never sends it. A saved ack updates the card's
// data in place and then calls onSaved, which re-renders the day (tally
// included) from that data (C2).
function openBlockedChooser(cl: TodayClean, row: HTMLElement, onSaved: () => void): void {
  const chooser = el('div', { class: 'blocked-chooser' })
  chooser.append(el('p', { class: 'blocked-chooser-title' }, "Couldn't do it. Why?"))
  const reasons = el('div', { class: 'blocked-reasons', role: 'group', 'aria-label': 'Reason' })
  // Ran out of time first (Liam, 22 Oct 2026): saved as Finish my day saves it, Other with those words (ui.ts).
  let picked: string | null = blockedChoiceOf(cl.blocked_reason, cl.blocked_note)
  let save: HTMLButtonElement | null = null
  const note = el('input', {
    class: 'blocked-note-input',
    type: 'text',
    maxlength: '300',
    placeholder: 'Add a note (optional)',
    'aria-label': 'Note (optional)',
  })
  // "Ran out of time. " goes in front of a note typed with it, so the box leaves room for it under the server's 300.
  const fitNote = (): void => { note.maxLength = picked === 'time' ? OUT_OF_TIME_ROOM : 300 }
  for (const [code, label] of BLOCKED_CHOICES) {
    const b = el('button', { type: 'button', class: 'blocked-reason', 'aria-pressed': String(picked === code) }, label)
    if (picked === code) b.classList.add('blocked-reason-picked')
    b.addEventListener('click', () => {
      picked = code
      for (const other of Array.from(reasons.querySelectorAll('.blocked-reason'))) {
        const on = other === b
        other.classList.toggle('blocked-reason-picked', on)
        other.setAttribute('aria-pressed', String(on))
      }
      if (save) save.disabled = false
      fitNote()
    })
    reasons.append(b)
  }
  note.value = blockedNoteText(cl.blocked_reason, cl.blocked_note)
  fitNote()
  const back = el('button', { type: 'button' }, 'Back')
  back.addEventListener('click', () => chooser.remove())
  save = el('button', { type: 'button', class: 'btn-primary' }, 'Save')
  if (!picked) save.disabled = true
  save.addEventListener('click', () => {
    if (!picked || save.disabled) return
    save.disabled = true
    // An emptied note must reach the server as null — omitting the field
    // would leave the old note standing (C3).
    const body: Record<string, unknown> = { version: cl.version, ...blockedSaveOf(picked, note.value) }
    void mutate<TodayClean>({
      label: `Couldn't do it: ${cl.customer_name}`,
      method: 'PATCH',
      path: `/api/cleans/${cl.id}`,
      body,
      // Edited elsewhere while the chooser was open: the server wrote nothing
      // — reload the day so the card shows reality, chooser and all, gone.
      onConflict: () => void load(),
    }).then((ack) => {
      if (ack.ok) {
        chooser.remove()
        const saved = ack.row as TodayClean | undefined
        if (saved) {
          cl.version = saved.version
          cl.blocked_reason = saved.blocked_reason
          cl.blocked_at = saved.blocked_at
          cl.blocked_note = saved.blocked_note
        }
        onSaved()
        toast("Marked couldn't do it")
        // What next opens straight away on the redrawn card: the owner's move, a worker's message (PLAN.md).
        afterBlockedSave(cl)
        undoPatch({ text: `Marked ${cl.customer_name} couldn't do it`, label: `Undo couldn't do it: ${cl.customer_name}`, path: `/api/cleans/${cl.id}`, ack, version: body.version as number,
          body: { blocked_reason: null }, after: () => void load() })
        return
      }
      save!.disabled = false
      if (ack.status === 0) {
        // Parked in the retry queue — it sends itself when signal returns,
        // and the flush reloads this screen (todayOnDataChanged).
        chooser.remove()
        toast('No signal. Saved on this phone to send later.')
      } else if (ack.status === 403) {
        chooser.remove()
        toast('This job has been given to someone else')
        void load()
      } else if (ack.status >= 400 && ack.status < 500 && ack.status !== 409) {
        window.alert(ack.error ?? 'rejected')
      }
    })
  })
  chooser.append(reasons, note, el('div', { class: 'blocked-chooser-actions' }, back, save))
  row.append(chooser)
}

// ---- a job that couldn't be done: moved to another day --------------------------
// Liam, 22 Oct 2026: "Couldnt yaschica because of job before whats the rearaange flow i dont see it?" (drawn and picked:
// docs/plans/rearrange/PLAN.md, option A). The owner moves it to a new day there and then, and their message asks if that
// day suits; a worker can't move a visit (the server refuses), so they can tell the customer someone will be in touch.

// "Couldn't do it · Ran out of time · 14:20", and the note typed with it.
function blockedLine(cl: TodayClean): HTMLElement {
  const line = el('p', { class: 'blocked-line' }, `Couldn't do it · ${blockedWords(cl.blocked_reason, cl.blocked_note)}${cl.blocked_at ? ` · ${blockedClock(cl.blocked_at)}` : ''}`)
  const note = blockedNoteText(cl.blocked_reason, cl.blocked_note)
  if (note) line.append(el('span', { class: 'blocked-line-note' }, note))
  return line
}

// The owner may move it from here: still booked, never started (a move wipes job times that Undo cannot put back:
// GPT-6.1's review), Couldn't do it saved for anything but Customer cancelled, on today or a day gone.
function mayRearrange(cl: TodayClean): boolean {
  return isOwnerToday() && cl.state === 'booked' && !!cl.blocked_reason && cl.blocked_reason !== 'customer_cancelled' && !cl.started_at && cl.date <= todayISO()
}

// Kept on the card's red line, so a redraw, a reload or "Leave it for now" never loses it (Sonnet's review).
function rearrangeButton(cl: TodayClean, row: HTMLElement): HTMLElement | null {
  if (!mayRearrange(cl)) return null
  const b = el('button', { type: 'button', class: 'rearrange-btn' }, 'Move to another day')
  b.addEventListener('click', () => {
    const open = row.querySelector('.rearrange-panel')
    if (open) open.remove()
    else openMovePanel(cl, row)
  })
  return b
}

// The day on screen, whichever view drew it.
let dayCleans: TodayClean[] = []
// The same house's other jobs that day still to do: they move with it, one message, one Undo (both reviews).
function houseMates(cl: TodayClean): TodayClean[] {
  return dayCleans.filter((o) => o.id !== cl.id && o.customer_id === cl.customer_id && o.date === cl.date && o.state === 'booked' && !o.started_at)
}

// A day's visits as this phone holds them, for its count: only a list of the whole round (Everyone, or a one-person
// round's own) is counted; "Just mine" would undercount the day (both reviews). Null when the phone doesn't hold it.
function heldDay(date: string): TodayClean[] | null {
  const key = worksAlone() ? cacheKeyFor(date) : `today:everyone:${date}`
  return readCache<TodayPayload>(key)?.data?.cleans ?? null
}

function openMovePanel(cl: TodayClean, row: HTMLElement): void {
  const first = firstName(cl.customer_name) || 'them'
  const panel = el('div', { class: 'rearrange-panel' })
  panel.append(el('p', { class: 'rearrange-title' }, `Move ${first} to another day?`))
  const mates = houseMates(cl)
  if (mates.length) panel.append(el('p', { class: 'muted rearrange-note' }, `Their ${mates.map((m) => jobWord(m).toLowerCase()).join(' and ')} ${mates.length === 1 ? 'moves' : 'move'} too.`))
  const days = el('div', { class: 'rearrange-days', role: 'group', 'aria-label': 'New day' })
  for (const date of moveDays(cl.date)) {
    const held = heldDay(date)
    const count = held ? dayLoad(held as unknown as Clean[], date, []).visits : null
    const b = el('button', { type: 'button', class: 'rearrange-day' }, el('span', {}, date === todayISO() ? 'Today' : dayLabel(date)), ...(count === null ? [] : [el('small', {}, visitsWords(count))]))
    b.addEventListener('click', () => {
      for (const d of Array.from(days.querySelectorAll<HTMLButtonElement>('button'))) d.disabled = true
      moveMissed(cl, date, () => { for (const d of Array.from(days.querySelectorAll<HTMLButtonElement>('button'))) d.disabled = false })
    })
    days.append(b)
  }
  panel.append(days)
  const time = shownVisitTime(cl)
  panel.append(el('p', { class: 'muted rearrange-note' }, time ? `Keeps the time, ${time}.` : 'No set time, as now.'))
  const leave = el('button', { type: 'button' }, 'Leave it for now')
  leave.addEventListener('click', () => panel.remove())
  const diary = el('a', { href: '#/schedule', class: 'btn-secondary rearrange-diary' }, 'Later day: Diary')
  panel.append(el('div', { class: 'rearrange-actions' }, leave, diary))
  row.append(panel)
}

const MISSED_NOTE = "couldn't do it"
const missedMoving = new Set<number>()
// The Diary's own move (schedule.ts moveToDay): the same save and Undo, and the same checks before it (parking hours,
// a day gone, gutters never after the windows day, one move at a time). No signal: nothing moves, so no day is promised
// before Roundly has it, and the button stays on the card (GPT-6.1 and Sonnet).
function moveMissed(cl: TodayClean, date: string, again: () => void, parkingOk = false): void {
  const first = firstName(cl.customer_name) || 'them'
  if (missedMoving.has(cl.id)) { toast(`Still moving ${first}. One moment.`); return }
  if (typeof navigator !== 'undefined' && navigator.onLine === false) { toast(`No signal. Move ${first} when you have signal: Move to another day stays on the card.`); again(); return }
  if (date < todayISO()) { toast('Pick today or a later day. A day that has gone cannot take a booking.'); again(); return }
  if (!parkingOk && askParking({ customer_name: cl.customer_name, parking: cl.parking_restrictions, duration_minutes: cl.duration_minutes ?? null }, visibleTime(cl), () => moveMissed(cl, date, again, true), date)) { again(); return }
  const visits = [cl, ...houseMates(cl)]
  if (severalJobsOn() && !visits.some((v) => (v.job_id ?? null) === null)) {
    // Gutters alone: never after the windows day, by the visits this phone holds for them.
    const theirs = moveDays(cl.date, todayISO()).flatMap((d) => (heldDay(d) ?? []).filter((o) => o.customer_id === cl.customer_id))
    const windowsDay = visits.some((v) => v.kind === 'gutter') ? windowsDayBefore(date, theirs.map((o) => ({ ...o, deleted_at: null })), todayISO()) : null
    if (windowsDay) { window.alert(`The windows are booked ${dayLabel(windowsDay)}. Gutters go on the windows day or the day before, never after. Move the gutters to ${dayLabel(windowsDay)} or earlier.`); again(); return }
  }
  // The same job already booked on that day (their next clean, say): never two of it on one day (GPT-6.1's review). Read
  // from the day as this phone holds it; the Diary's move is the way when it doesn't.
  const already = (heldDay(date) ?? []).find((o) => o.customer_id === cl.customer_id && !visits.some((v) => v.id === o.id) && visits.some((v) => (v.job_id ?? null) === (o.job_id ?? null)))
  if (already) { window.alert(`${first} already has ${jobWord(already).toLowerCase()} booked ${dayLabel(date)}. Pick another day, or move it in the Diary.`); again(); return }
  const from = cl.date
  const reason = blockedChoiceOf(cl.blocked_reason, cl.blocked_note)
  // The reason goes in the visit's note: the server clears Couldn't do it from a moved visit (Sonnet's review).
  // A note stamp, as the sorry message's: "⏱ 14:20 — couldn't do it (ran out of time), moved to Fri 23 Oct".
  const line = `⏱ ${noteClock(new Date())} — ${MISSED_NOTE} (${blockedWords(cl.blocked_reason, cl.blocked_note).toLowerCase()}), moved to ${dayLabel(date)}`
  missedMoving.add(cl.id)
  void (async () => {
    const placements: Placement[] = []
    let queued = false
    // Onto a day set to someone, the visit became theirs (the server, 27 Oct 2026): said on the Undo bar, as the Diary does.
    let whose = ''
    for (const v of visits) {
      const before = { date: v.date, confirmed: v.confirmed, time_agreed_for: v.time_agreed_for ?? null, note: v.note ?? null }
      const ack = await mutate<TodayClean>({
        label: `Move: ${v.customer_name} to ${dayLabel(date)}`,
        method: 'PATCH',
        path: `/api/cleans/${v.id}`,
        body: { version: v.version, date, adopt_booking: true, note_append: line },
        onConflict: () => void load(),
      })
      if (ack.ok || ack.status === 0) {
        queued ||= ack.status === 0
        const row = ack.row as { version?: number } | undefined
        if (v.id === cl.id) whose = nowWhose({ worker: cl.phone_of ?? null }, ack.row)
        placements.push({ kind: 'move', id: v.id, version: typeof row?.version === 'number' ? row.version : v.version + 1, before })
        continue
      }
      if (ack.status >= 400 && ack.status < 500 && ack.status !== 409) window.alert(ack.error ?? 'rejected')
      break
    }
    missedMoving.delete(cl.id)
    if (!placements.length) { again(); void load(); return }
    // One Undo puts every visit moved back on its day, Couldn't do it and all (undo_restore, as the Diary's Undo).
    let undone = false
    const undo = (): void => {
      if (undone) return
      undone = true
      forgetToTell(cl.id)
      for (const p of placements) void mutate({ label: `Undo move: ${cl.customer_name} back to ${dayLabel(from)}`, method: 'PATCH', path: `/api/cleans/${p.id}`, body: { version: p.version, ...p.before, undo_restore: true }, onAck: () => void load(), onConflict: () => void load() })
    }
    const tell: ToTell = { visitId: cl.id, customerId: cl.customer_id, name: cl.customer_name, from, to: date, reason, jobIds: visits.filter((v) => placements.some((p) => p.id === v.id)).map((v) => v.job_id ?? null), on: todayISO() }
    keepToTell(tell)
    void load()
    if (queued || placements.length < visits.length) {
      offerUndo(`Moved ${cl.customer_name} to ${dayLabel(date)}${whose} · not told yet`, undo)
      if (queued) toast(`Saved on this phone. Message ${first} once it sends: they wait at the top of Today.`)
      // A house split by a visit changed elsewhere says so, rather than a plain "Moved" (GPT-6.1's review).
      else {
        const left = visits.filter((v) => !placements.some((p) => p.id === v.id)).map((v) => jobWord(v).toLowerCase())
        toast(`Their ${left.join(' and ')} didn't move: changed on another phone. Move ${left.length === 1 ? 'it' : 'them'} in the Diary, then message ${first}.`)
      }
      return
    }
    openTellMissed(tell, placements, undo)
  })()
}

// Their message, in Messages' own pop-up and send (rebook.ts askAboutDay with `missed`): a recorded send, so Messages and
// the Diary read it as asked, and its Undo (or Cancel) puts the visit back too. Put away unanswered, they wait as
// "Not told yet" at the top of Today.
function openTellMissed(t: ToTell, placements: Placement[] = [], undo?: () => void): void {
  askAboutDay(t.customerId, t.to, {
    visitId: t.visitId,
    missed: { from: t.from, reason: t.reason, jobIds: t.jobIds, placements },
    ...(undo ? { onCancel: undo, cancelLabel: `Put back on ${dayLabel(t.from)}` } : {}),
    onDone: () => { forgetToTell(t.visitId); void load() },
    onLater: () => {
      if (undo) offerUndo(`Moved ${t.name} to ${dayLabel(t.to)} · not told yet`, undo)
      void load()
    },
  })
}

// The moves not told yet, at the top of the owner's Today.
function toTellRows(container: HTMLElement): void {
  if (!isOwnerToday() || selectedDate !== todayISO()) return
  for (const t of toTellList()) {
    const message = el('button', { type: 'button', class: 'btn-primary' }, 'Message')
    message.addEventListener('click', () => openTellMissed(t))
    const told = el('button', { type: 'button' }, 'I told them')
    told.addEventListener('click', () => { forgetToTell(t.visitId); void reloadInPlace() })
    container.append(el('div', { class: 'to-tell-row' },
      el('p', {}, el('strong', {}, t.name), ` moved to ${dayLabel(t.to)} · not told yet`),
      el('div', { class: 'to-tell-actions' }, told, message)))
  }
}

// A worker's: "Message Yaschica" on the red line, then "Message again" once one has been opened.
function sorryButton(cl: TodayClean, row: HTMLElement, onNote: (note: string | null) => void): HTMLElement | null {
  if (isOwnerToday() || cl.state !== 'booked' || !cl.blocked_reason || cl.blocked_reason === 'customer_cancelled' || cl.date > todayISO()) return null
  if (!cleanPhone(cl.phone) || lateWay(cl) === 'none') return null
  const first = firstName(cl.customer_name) || 'them'
  const sent = (cl.note ?? '').includes(SORRY_NOTE)
  const b = el('button', { type: 'button', class: 'late-btn sorry-btn ic ic-contact' }, sent ? 'Message again' : `Message ${first}`)
  b.addEventListener('click', () => {
    const open = row.querySelector('.sorry-chooser')
    if (open) open.remove()
    else openSorry(cl, row, onNote)
  })
  return b
}

const SORRY_NOTE = 'sorry message opened to'
// Opened like Running late: the phone's own WhatsApp or Messages, the text laid out first. The note says it was opened,
// not that it arrived (GPT-6.1's review: an opened app is not a sent message).
function openSorry(cl: TodayClean, row: HTMLElement, onNote: (note: string | null) => void): void {
  const first = firstName(cl.customer_name)
  const businessName = getMe()?.businessName || businessSettings().business_name
  const text = missedWorkerMessage(cl.customer_name, { from: cl.date, reason: blockedChoiceOf(cl.blocked_reason, cl.blocked_note) }, businessName)
  const chooser = el('div', { class: 'late-chooser sorry-chooser' })
  chooser.append(el('p', { class: 'late-chooser-title' }, `Message ${first || 'them'}?`))
  chooser.append(el('p', { class: 'muted' }, 'The owner will move this job to another day.'))
  chooser.append(el('p', { class: 'late-preview' }, text))
  const wantsWhatsApp = lateWay(cl) === 'whatsapp'
  const byWhatsApp = wantsWhatsApp && whatsappHref(cl.phone, 'x') !== null
  if (wantsWhatsApp && !byWhatsApp) chooser.append(el('p', { class: 'muted late-way-note' }, `${first || 'They'} asked for WhatsApp, but this number can't get WhatsApp. This goes as a text.`))
  const no = el('button', { type: 'button' }, 'No message')
  no.addEventListener('click', () => chooser.remove())
  const open = el('button', { type: 'button', class: 'btn-primary' }, byWhatsApp ? 'Open WhatsApp' : 'Open Messages')
  open.addEventListener('click', () => {
    const href = byWhatsApp ? whatsappHref(cl.phone, text) : smsHref(cl.phone, text, navigator.userAgent)
    if (!href) return
    openOwnSend({ href, name: cl.customer_name, sent: () => {
      chooser.remove()
    void mutate<TodayClean>({
      label: `Sorry message: ${cl.customer_name}`,
      method: 'PATCH',
      path: `/api/cleans/${cl.id}`,
      body: { version: cl.version, note_append: `⏱ ${noteClock(new Date())} — ${SORRY_NOTE} ${first || cl.customer_name}` },
      onConflict: () => void load(),
    }).then((ack) => {
      if (!ack.ok) return
      const saved = ack.row as TodayClean | undefined
      if (saved) {
        cl.version = saved.version
        onNote(saved.note)
      }
    })
    } })
  })
  chooser.append(el('div', { class: 'late-chooser-actions' }, no, open))
  row.append(chooser)
}

// After a Couldn't do it is saved, its next step opens on the card the day has just redrawn.
function afterBlockedSave(cl: TodayClean): void {
  const row = Array.from(document.querySelectorAll<HTMLElement>('.today-clean')).find((r) => r.dataset.cleanId === String(cl.id))
  if (!row) return
  if (mayRearrange(cl)) openMovePanel(cl, row)
  else if (!isOwnerToday() && row.querySelector('.sorry-btn') && !(cl.note ?? '').includes(SORRY_NOTE)) openSorry(cl, row, () => void reloadInPlace())
}

// ---- the "Running late" chooser -----------------------------------------------

// Same in-card pattern as the blocked chooser: a Late/Early toggle, minute
// chips (10–60) or a small "other" box, the text laid out before anything is
// sent, then Open Messages / Back. Open Messages hands the text to the
// phone's OWN Messages app (Roundly sends nothing itself) and queues the
// ⏱ note append like any van write — if the note cannot save (no signal,
// note full), the text still opens; the queue chip carries the rest.
// Defaults: Late · 30 — the two taps a worker most often needs.
function openLateChooser(cl: TodayClean, row: HTMLElement, onNote: (note: string | null) => void): void {
  const first = firstName(cl.customer_name)
  const workerName = getMe()?.workerName || 'me'
  // No Dropby fallback: getMe().businessName is always the same value as
  // businessSettings().business_name once settings have loaded (me.ts's Me
  // type comment), so the settings copy is the one source once either is
  // missing rather than a second hardcoded name.
  const businessName = getMe()?.businessName || businessSettings().business_name
  let early = false
  let minutes = 30
  const chooser = el('div', { class: 'late-chooser' })
  chooser.append(el('p', { class: 'late-chooser-title' }, 'Running late?'))

  const preview = el('p', { class: 'late-preview' })
  const message = (): string => lateMessage({ first, workerName, businessName, minutes, early, kind: cl.kind })
  const paintPreview = (): void => {
    preview.textContent = message()
  }

  const toggle = el('div', { class: 'late-toggle', role: 'group', 'aria-label': 'Late or early' })
  const toggleBtn = (label: string, isEarly: boolean): HTMLButtonElement => {
    const b = el(
      'button',
      { type: 'button', class: 'late-toggle-btn', 'aria-pressed': String(isEarly === early) },
      label,
    )
    if (isEarly === early) b.classList.add('late-picked')
    b.addEventListener('click', () => {
      early = isEarly
      for (const other of Array.from(toggle.querySelectorAll('.late-toggle-btn'))) {
        const on = other === b
        other.classList.toggle('late-picked', on)
        other.setAttribute('aria-pressed', String(on))
      }
      paintPreview()
    })
    return b
  }
  toggle.append(toggleBtn('Late', false), toggleBtn('Early', true))

  const chips = el('div', { class: 'late-chips', role: 'group', 'aria-label': 'Minutes' })
  const chipButtons: HTMLButtonElement[] = []
  const pickChip = (chip: HTMLButtonElement | null): void => {
    for (const c of chipButtons) {
      const on = c === chip
      c.classList.toggle('late-picked', on)
      c.setAttribute('aria-pressed', String(on))
    }
  }
  for (const m of [10, 15, 20, 30, 45, 60]) {
    const b = el('button', { type: 'button', class: 'late-chip', 'aria-pressed': String(m === minutes) }, String(m))
    if (m === minutes) b.classList.add('late-picked')
    b.addEventListener('click', () => {
      minutes = m
      other.value = ''
      pickChip(b)
      paintPreview()
    })
    chipButtons.push(b)
    chips.append(b)
  }
  const other = el('input', {
    class: 'late-other-input',
    type: 'number',
    inputmode: 'numeric',
    min: '1',
    max: '240',
    placeholder: 'other',
    'aria-label': 'Other minutes',
  })
  other.addEventListener('input', () => {
    const v = Math.round(Number(other.value))
    if (!Number.isFinite(v) || v < 1) return
    minutes = Math.min(240, v)
    pickChip(null)
    paintPreview()
  })

  const back = el('button', { type: 'button' }, 'Back')
  back.addEventListener('click', () => chooser.remove())
  // A text, as it always was; WhatsApp only when the customer asked for WhatsApp (contact.ts lateWay).
  // A WhatsApp customer whose number WhatsApp cannot reach (a landline, "no WA") gets a text, and the chooser
  // SAYS so above the button: never a quiet switch (Astra, 30 Sep).
  const wantsWhatsApp = lateWay(cl) === 'whatsapp'
  const byWhatsApp = wantsWhatsApp && whatsappHref(cl.phone, 'x') !== null
  const openBtn = el('button', { type: 'button', class: 'btn-primary' }, byWhatsApp ? 'Open WhatsApp' : 'Open Messages')
  openBtn.addEventListener('click', () => {
    const href = byWhatsApp ? whatsappHref(cl.phone, message()) : smsHref(cl.phone, message(), navigator.userAgent)
    if (!href) return // unreachable: the button is hidden without a number
    const line = lateNoteLine(new Date(), first, minutes, early)
    openOwnSend({ href, name: cl.customer_name, sent: () => {
      chooser.remove()
    void mutate<TodayClean>({
      label: `Running late text: ${cl.customer_name}`,
      method: 'PATCH',
      path: `/api/cleans/${cl.id}`,
      body: { version: cl.version, note_append: line },
      // Edited elsewhere while the chooser was open: the server wrote nothing
      // — reload the day so the card shows reality.
      onConflict: () => void load(),
    }).then((ack) => {
      if (!ack.ok) return // queued/conflict surfaces in the queue chip as usual
      const saved = ack.row as TodayClean | undefined
      if (saved) {
        cl.version = saved.version
        onNote(saved.note)
      }
      toast('Visit noted, customer told')
    })
    } })
  })

  paintPreview()
  chooser.append(
    toggle,
    chips,
    other,
    preview,
    ...(wantsWhatsApp && !byWhatsApp ? [el('p', { class: 'muted late-way-note' }, `${first || 'They'} asked for WhatsApp, but this number can't get WhatsApp. This goes as a text.`)] : []),
    el('div', { class: 'late-chooser-actions' }, back, openBtn),
  )
  row.append(chooser)
}

// ---- the selected day ----------------------------------------------------------

// The job after this one that could still be told the van is late: later in
// the day's order, today, not started, not marked "Couldn't do it", and with
// a number a text can go to.
// Several jobs on: never the house the worker is already at (its other job is the same stop, PLAN 4).
function nextToWarn(visits: TodayClean[], cl: TodayClean): TodayClean | undefined {
  const at = visits.indexOf(cl)
  const sameHouse = severalJobsOn()
  return visits.slice(at + 1).find((v) => v.state === 'booked' && v.date === todayISO() && !v.started_at && !v.blocked_reason && cleanPhone(v.phone) && !(sameHouse && v.customer_id === cl.customer_id))
}


// One stop for the house (E1): the name, the address and the access notes once, then each job with its own Start, tick,
// price and ⋯. No "finish both" (Liam Q9: each job ticked done and paid on its own); the windows keep their own Start
// when the gutters come first (Astra: the order is not a rule). Counts are jobs; the map counts the house once.
function renderHouse(stop: TodayClean[], visits: TodayClean[], rerender: () => void, celebrate: Set<number>, place: StopPlace = { n: null, open: true }): HTMLElement {
  const first = stop[0]!
  const house = el('section', { class: `clean-row today-clean today-house today-stop ${place.open ? 'is-open' : 'is-folded'}`, 'aria-label': `${first.customer_name}, ${stop.length} jobs` })
  house.dataset.stopId = String(first.id)
  if (place.focus) house.classList.add('is-focus')
  else house.append(stopOpener(house, `${first.customer_name}, ${stop.length} jobs`))
  const allDone = stop.every((c) => c.state === 'done')
  if (allDone) house.classList.add('today-done', 'clean-paid')
  if (allDone) house.append(el('span', { class: 'done-mark ic ic-check', 'aria-hidden': 'true' }))
  else if (place.n !== null) house.append(stopNum(place.n, stop.every((c) => c.state === 'booked' && !!c.blocked_reason)))
  const head = el('div', { class: 'sched-clean-main' })
  head.append(el('span', { class: 'sched-clean-line' }, nameNode(first.customer_id, first.customer_name), ...(() => { const f = stopFlag(stop); return f ? [f] : [] })(), el('span', { class: 'today-house-count' }, `${stop.length} jobs`)))
  const where0 = first.address ? splitAccessWarning(first.address) : null
  const where = where0 ? fullAddress(first, where0.addr) : first.postcode ?? ''
  if (where) {
    const href = mapsHref(first)
    head.append(href
      ? el('a', { href, target: '_blank', rel: 'noopener', class: 'today-address today-address-link ic ic-pin', 'aria-label': `Open ${where} in Maps` }, where)
      : el('span', { class: 'today-address ic ic-pin' }, where))
  }
  head.append(el('span', { class: 'stop-street' }, [where0 ? where0.addr.split(',')[0]!.trim() : first.postcode ?? '', stop.map(jobWord).join(', then ')].filter(Boolean).join(' · ')))
  if (where0?.warn) head.append(el('span', { class: 'access-warn today-warn' }, where0.warn))
  head.append(el('p', { class: 'today-house-line' }, `${stop.length} jobs here: ${stop.map(jobWord).join(', then ')}`))
  const clip = first.date < todayISO() ? null : noteClip(first, house)
  if (clip) head.append(clip)
  house.append(head)
  const notes = first.date < todayISO() ? [] : noteRows(first)
  if (notes.length) {
    const box = el('div', { class: 'access-notes' }, el('strong', {}, 'Access & notes'))
    box.append(...notes)
    house.append(box)
  }
  const jobs = el('div', { class: 'today-house-jobs' })
  for (const cl of stop) {
    const card = renderCard(cl, rerender, nextToWarn(visits, cl), true, { n: null, open: true, focus: !!place.focus })
    if (celebrate.has(cl.id)) card.classList.add('done-enter')
    jobs.append(card)
  }
  house.append(jobs)
  // Directions and Contact once for the house (Astra: one contact per house), as small squares under its jobs.
  const tools = el('div', { class: 'today-primary stop-actions' })
  const directions = mapsHref(first)
  if (directions) tools.append(el('a', { href: directions, target: '_blank', rel: 'noopener', class: 'stop-square directions-link ic ic-navigate', 'aria-label': `Directions to ${first.customer_name}` }, 'Directions'))
  const contact = contactButton({ name: first.customer_name, phone: first.phone, message_by: first.message_by }, 'stop-square')
  if (contact) tools.append(contact.button)
  if (tools.childElementCount) house.append(tools)
  if (contact?.choices) house.append(contact.choices)
  return house
}

// Word for word the diary's group labels (schedule.ts), so the two screens
// read the same.

// The day's groups (day-order.ts bandOf): visits with a time, then those with none whose day is agreed or that are
// done, then the ones whose day is not agreed, under the Diary's own heading for them (bug hunt 8 Oct 2026: "Confirmed,
// no time yet" also held finished work nobody had confirmed). Each visit says its own words (todayVisitBadge).
const BAND_LABELS: Record<number, string> = { 0: 'Times set', 1: 'No set time', 2: 'Pencilled visits' }

// A visit's words on Today, the Diary's (visit-words.ts) for the owner, whose Today carries the customer's facts
// (GET /api/today). A worker's phone has no messaging record, so it says only what it knows: an agreed day and its
// time, or that Roundly suggested it; never "not asked yet" about someone who may well have been asked (bug hunt
// 8 Oct 2026, owed-today 6).
type TodayFacts = TodayClean & Partial<Pick<Clean, 'customer_status' | 'customer_list' | 'customer_messages_shelf' | 'customer_message_reply' |
  'customer_message_reply_on' | 'customer_message_reply_dates' | 'customer_message_reply_job_id' | 'customer_message_replies' | 'customer_follow_up_on' |
  'customer_rebook_messages' | 'customer_rebook_messaged_on' | 'customer_last_clean' | 'job_rebook_messages' | 'job_rebook_messaged_on' | 'moved_from' | 'moved_on' | 'moved_told'>> &
  { customer_contact_note?: string | null }
// On Today a visit is happening, so no words about whether its day or time was agreed (Liam, 13 Oct 2026: "a bunch of
// people not confirmed or agreed time surely they should just be marked as confirmed to show up here? Otherwise confusing
// for the worker"); only where the customer stands ("Paused · check before going"). A suggestion or a contact reminder
// still says what it is, as it is not a visit to do.
function todayVisitBadge(cl: TodayFacts): HTMLElement | null {
  const visit = { ...cl, source: cl.source ?? null }
  const customer = cl.customer_rebook_messages !== undefined ? diaryCustomer(cl as unknown as Clean, { contact_note: cl.customer_contact_note ?? null }) : null
  if (isSuggestion(visit) || isContact(visit)) return visitBadge(visit, customer)
  const words = visitWords(visit, customer)
  const text = [words.context, words.need === 'check' ? 'check before going' : ''].filter(Boolean).join(' · ')
  return text ? el('span', { class: 'badge visit-words-note badge-unconfirmed' }, text) : null
}

// How someone else's stop is going, on the right of it where a tick would be (B12: view only, no buttons).
// Nothing said until something happens (Liam, 4 Nov 2026, design H: "Not started" on every row said nothing).
function viewStatus(cl: TodayClean): string {
  if (cl.state === 'done') return 'Done'
  if (cl.state === 'booked' && cl.blocked_reason) return "Couldn't do it"
  if (cl.finished_at) return `Finished ${clockOf(cl.finished_at)}`
  if (cl.started_at) return `Started ${clockOf(cl.started_at)}`
  return ''
}

// A job on someone else's phone, as the owner watching sees it: who, where,
// how it is going. No Start, Done or other buttons, so nothing is done on
// their behalf by mistake (Liam, 2026-09-25; B12). Folded to a line on the numbered line; tapped, it shows the rest.
function renderViewOnlyCard(cl: TodayClean, place: StopPlace = { n: null, open: true }): HTMLElement {
  const row = el('div', { class: `clean-row clean-${cl.state} today-clean today-view-only today-stop ${place.open ? 'is-open' : 'is-folded'}`, 'data-clean-id': String(cl.id) })
  row.append(stopOpener(row, `${cl.customer_name}, ${jobWord(cl)}`))
  if (cl.state === 'done') {
    // Every finished job on Today goes green, paid or not (Liam, 14 Oct 2026: "for the today you want to see and have
    // the satisfaction of all tiles turning green once complete"); its badge still says Not paid. The Diary keeps green
    // for paid and amber for owed.
    row.classList.add('clean-paid', 'today-done')
    row.append(el('span', { class: 'done-mark ic ic-check', 'aria-hidden': 'true' }))
  } else if (place.n !== null) row.append(stopNum(place.n, cl.state === 'booked' && !!cl.blocked_reason))
  const live = cl.state === 'booked' && !!cl.started_at && !cl.finished_at && !cl.blocked_reason
  if (live) row.classList.add('today-view-live')
  const main = el('div', { class: 'sched-clean-main' })
  const line = el('span', { class: 'sched-clean-line' })
  const viewTime = stopTime(cl)
  if (viewTime) line.append(viewTime)
  else if (shownVisitTime(cl)) line.append(el('span', { class: 'clean-time' }, shownVisitTime(cl)!))
  line.append(nameNode(cl.customer_id, cl.customer_name))
  // Everyone shows the same red "!" or grey note as the helper's own Today (GPT-6.1's notes review 18).
  const viewFlag = cl.state !== 'done' ? stopFlag(cl) : null
  if (viewFlag) line.append(viewFlag)
  const viewPriced = linePrice(cl, false)
  if (viewPriced?.price) line.append(viewPriced.price)
  main.append(line)
  const place0 = cl.address ? splitAccessWarning(cl.address) : null
  const where = place0 ? fullAddress(cl, place0.addr) : cl.postcode ?? ''
  if (where) {
    const href = mapsHref(cl)
    main.append(href
      ? el('a', { href, target: '_blank', rel: 'noopener', class: 'today-address today-address-link ic ic-pin', 'aria-label': `Open ${where} in Maps` }, where)
      : el('span', { class: 'today-address ic ic-pin' }, where))
  }
  main.append(el('span', { class: 'stop-street' }, [place0 ? place0.addr.split(',')[0]!.trim() : cl.postcode ?? '', jobWord(cl)].filter(Boolean).join(' · ')))
  const sub = el('span', { class: 'clean-badges' })
  if (cl.kind) sub.append(el('span', { class: 'badge badge-kind' }, jobWord(cl)))
  if (viewPriced) sub.append(...viewPriced.rest.map(wholePounds))
  else if (cl.extras_json) sub.append(...extraBadges(cl.extras_json, false))
  const viewPart = partBadge(cl)
  if (viewPart) sub.append(viewPart)
  if (moneyPending(cl)) sub.append(el('span', { class: 'badge credit-badge credit-pending' }, 'Credit being worked out'))
  else if (fromCredit(cl) > 0 && cl.amount_pence != null) sub.append(el('span', { class: 'badge credit-badge' }, `${money(fromCredit(cl))} from credit · ${money(toCollect(cl))} to collect`))
  const agreement = cl.state === 'booked' ? todayVisitBadge(cl) : null
  if (agreement) sub.append(agreement)
  if (cl.state === 'done') sub.append(paymentWordsBadge(cl))
  main.append(sub)
  const alsoOwesOwner = alsoOwesWords(cl, money)
  if (alsoOwesOwner && cl.state === 'booked') main.append(el('p', { class: 'also-owes' }, alsoOwesOwner))
  if (cl.note) main.append(noteNode(cl.note))
  if (cl.state === 'booked' && cl.blocked_reason) {
    main.append(blockedLine(cl))
    // Someone else's job on Everyone: the owner can still move it (the worker can't).
    const move = rearrangeButton(cl, row)
    if (move) main.append(move)
  }
  const times = jobTimesLine(cl)
  if (times) main.append(el('p', { class: 'job-times-line ic ic-clock' }, times))
  const status = viewStatus(cl)
  row.append(main)
  if (status) { row.classList.add('has-status'); row.append(el('span', { class: 'stop-status' }, status)) }
  return row
}

// ---- the day's tiles (B1) ---------------------------------------------------------------------------------------------
// Done out of booked in a ring, the money taken of the day's, the stops left on a map, and Finish my day, there all day
// (Liam, 24 Oct 2026: early, it asks why for each job left, with today's reasons). Someone who doesn't see prices sees
// their jobs this week instead of money, and a tap shows their whole count (job-count.ts). The figures change at once;
// only the ring settles, over a quarter of a second, when a job is done (the movement set).
const ringBefore = new Map<string, { done: number; total: number }>()
function tileRing(done: number, total: number, key: string): SVGElement {
  const ns = 'http://www.w3.org/2000/svg'
  const svg = document.createElementNS(ns, 'svg')
  svg.setAttribute('viewBox', '0 0 34 34'); svg.setAttribute('width', '34'); svg.setAttribute('height', '34'); svg.setAttribute('aria-hidden', 'true')
  const circle = (extra: Record<string, string>): SVGElement => {
    const c = document.createElementNS(ns, 'circle')
    for (const [k, v] of Object.entries({ cx: '17', cy: '17', r: '13', fill: 'none', 'stroke-width': '4', ...extra })) c.setAttribute(k, v)
    return c
  }
  svg.append(circle({ stroke: '#e3e8ee' }))
  const was = ringBefore.get(key)
  ringBefore.set(key, { done, total })
  const settle = !!was && was.total === total && was.done < done && !reducedMotion()
  const dash = (n: number): string => `${(81.7 * Math.min(1, total ? n / total : 0)).toFixed(1)} 81.7`
  if (done > 0) {
    const arc = circle({ stroke: '#188038', 'stroke-dasharray': dash(settle && was ? was.done : done), transform: 'rotate(-90 17 17)', 'stroke-linecap': 'round' })
    svg.append(arc)
    if (settle && typeof requestAnimationFrame === 'function') {
      (arc as unknown as HTMLElement).style.transition = 'stroke-dasharray 250ms cubic-bezier(.2,.8,.2,1)'
      requestAnimationFrame(() => requestAnimationFrame(() => arc.setAttribute('stroke-dasharray', dash(done))))
    }
  }
  return svg
}

function tile(drawing: SVGElement, big: string, small: string, cls = '', act?: () => void, label?: string): HTMLElement {
  const parts = [el('span', { class: 'today-tile-mark' }, drawing), el('strong', { class: 'today-tile-big' }, big), el('span', { class: 'today-tile-small' }, small)]
  if (!act) return el('div', { class: `today-tile ${cls}`.trim() }, ...parts)
  const b = el('button', { type: 'button', class: `today-tile ${cls}`.trim(), ...(label ? { 'aria-label': label } : {}) }, ...parts)
  b.addEventListener('click', act)
  return b
}

// The stops on the map, numbered as the list numbers them; another person's by their initial and place (C4: Dave's D1,
// D2); a job under way dark (C2).
function mapStops(stops: TodayClean[][], numbered: Map<number, number>): PinStop[] {
  const counts = new Map<string, number>()
  return stops.map((s) => {
    const c = s[0]!
    const mine = numbered.has(c.id)
    let label: string | null = null
    if (!mine) {
      const who = c.phone_of?.trim() || '?'
      const k = (counts.get(who) ?? 0) + 1
      counts.set(who, k)
      label = `${who.slice(0, 1).toUpperCase()}${k}`
    }
    return { id: c.id, customer_id: c.customer_id, customer_name: c.customer_name, customer_address: c.address, customer_postcode: c.postcode,
      customer_lat: c.lat ?? null, customer_lng: c.lng ?? null, customer_maps_link: c.maps_link ?? null, customer_geocoded_postcode: c.geocoded_postcode ?? null, time_slot: c.time_slot, state: s.every((v) => v.state === 'done') ? 'done' : 'booked',
      blocked_reason: s.every((v) => v.state === 'booked' && !!v.blocked_reason) ? c.blocked_reason ?? null : null,
      // A stop done shows its tick, as the Diary's map does: no number, no label.
      n: mine && !s.every((v) => v.state === 'done') ? numbered.get(c.id)! : null,
      label: s.every((v) => v.state === 'done') ? null : label, live: s.some((v) => v.state === 'booked' && !!v.started_at && !v.finished_at && !v.blocked_reason && v.date === todayISO()) }
  })
}
const stopLeft = (s: TodayClean[]): boolean => s.some((c) => c.state === 'booked' && !c.blocked_reason && !c.finished_at)

interface TileDay { date: string; all: TodayClean[]; own: TodayClean[] | null; numbered: Map<number, number>; facts: JobCountFacts | null; rerender: () => void }
function dayTiles(d: TileDay): { tiles: HTMLElement; count: HTMLElement | null } {
  const { done, total } = dayVisitCounts(d.all)
  const view = showingMine() ? 'mine' : 'everyone'
  const tiles = el('div', { class: 'today-tiles' })
  // Design H on a phone (Liam, 4 Nov 2026, https://claude.ai/artifact/74zWf8kUcfCMSk2dJpPpKE): one light card for the
  // day, the ring, the money worked, what was taken and what is not paid, and Map. Someone who doesn't see prices keeps
  // the tiles. Worked and taken are different amounts, so both are said (Astra).
  const card = !deskWide() && !d.facts && !pricesHidden()
  if (card) {
    const counted = d.all.filter((c) => !(c.state === 'booked' && c.blocked_reason))
    const dayTotal = counted.reduce((sum, c) => sum + (c.amount_pence ?? 0), 0)
    const finished = d.all.filter((c) => c.state === 'done')
    const worked = finished.reduce((sum, c) => sum + (c.amount_pence ?? 0), 0)
    const taken = finished.reduce((sum, c) => sum + visitReceived(c), 0)
    const owed = finished.reduce((sum, c) => sum + owedOn(c), 0)
    const small = el('span', { class: 'today-progress-small' }, done ? `${pounds(taken)} taken` : `${pounds(dayTotal)} today`)
    if (owed) small.append(' · ', el('b', { class: 'today-progress-owed' }, `${pounds(owed)} not paid`))
    tiles.classList.add('is-progress')
    tiles.append(el('div', { class: 'today-progress' },
      el('span', { class: 'today-progress-ring' }, tileRing(done, total, `${d.date}|${view}`), el('b', {}, `${done}/${total}`)),
      el('span', { class: 'today-progress-words' }, el('strong', {}, `${pounds(worked)} worked`), small)))
  } else tiles.append(tile(tileRing(done, total, `${d.date}|${view}`), `${done}/${total}`, 'done', 'is-quiet'))
  let count: HTMLElement | null = null
  if (card) { /* the money is on the card */ } else if (d.facts) {
    // The week's jobs, and a tap shows this month and the best day too (Astra: the count card's other numbers keep a way in).
    count = jobCountCard(d.facts, '', false)
    count.hidden = true
    const card = count
    tiles.append(tile(lineDrawing('calendar', 20), String(d.facts.week), 'jobs this week', '', () => { card.hidden = !card.hidden }, `${d.facts.week} jobs this week: show my count`))
  } else if (!pricesHidden()) {
    const counted = d.all.filter((c) => !(c.state === 'booked' && c.blocked_reason))
    const total = counted.reduce((sum, c) => sum + (c.amount_pence ?? 0), 0)
    const taken = d.all.filter((c) => c.state === 'done').reduce((sum, c) => sum + visitReceived(c), 0)
    tiles.append(tile(lineDrawing('pound', 20), money(taken).replace(/\.00$/, ''), total ? `taken of ${money(total).replace(/\.00$/, '')}` : 'taken'))
  }
  // Everyone's stops on Everyone (B1: the tile counts the whole day's), the person's own on Just mine.
  const mapAll = dayStops(d.all, severalJobsOn())
  const left = mapAll.filter(stopLeft).length
  // On a computer the map sits beside the stops (C1), so no tile opens it.
  const openMap = (): void => { void import('../day-map-panel').then((panel) => panel.openDayMap(mapStops(mapAll, d.numbered), dayLabel(d.date))) }
  if (card) {
    const map = el('button', { type: 'button', class: 'today-progress-map', 'aria-label': `${left} ${left === 1 ? 'stop' : 'stops'} left: the day on a map` }, 'Map')
    map.addEventListener('click', openMap)
    tiles.querySelector('.today-progress')?.append(map)
  } else if (!deskWide()) tiles.append(tile(lineDrawing('pin', 20), String(left), 'Map', '', openMap, `${left} ${left === 1 ? 'stop' : 'stops'} left: the day on a map`))
  // On a computer, the owner watching the round: who is out with jobs today (C1, C6); a tap opens the day in the Diary,
  // where Out today is picked.
  if (deskWide() && isOwnerToday() && !worksAlone() && !showingMine()) {
    const out = [...new Set(d.all.map((c) => c.phone_of?.trim() || '').filter((n) => n && n !== ownName))]
    if (out.length) tiles.append(tile(lineDrawing('person', 20), out.length === 1 ? out[0]! : String(out.length), out.length === 1 ? 'out today' : 'out working', '', () => { window.location.hash = `#/schedule?from=${d.date}` }, `${out.join(', ')} out: open the day in the Diary`))
  }
  // Finish my day: the person's own day, today or a day of the last week (day-close.ts finishDayCard's own rule).
  const today = todayISO()
  const own = (d.own ?? []).filter((v) => !pencilledIn(v))
  if (getMe()?.workerName?.trim() && own.length && d.date <= today && d.date >= addDaysISO(today, -7)) {
    const ready = own.every((v) => v.state !== 'booked' || !!v.blocked_reason)
    const finish = tile(lineDrawing(ready ? 'tick' : 'flag', 20), '', 'Finish my day', `today-tile-finish${ready ? ' is-ready' : ''}`, () => {
      // Finished already: its card (the invoice, Add photos) is shown instead of starting again.
      const finished = document.querySelector<HTMLElement>('#today-day .day-close-card.is-finished')
      if (finished) { finished.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' }); return }
      openFinishDay(d.date, own, d.rerender)
    })
    tiles.append(finish)
  }
  if (tiles.childElementCount === 3) tiles.classList.add('is-three')
  tiles.setAttribute('style', `--tiles: ${tiles.childElementCount}`)
  return { tiles, count }
}

// One person's heading on their stops (B1: "You · 0 of 3", "£0 of £170").
function groupHead(label: string, jobs: TodayClean[], tag: 'div' | 'summary' = 'div', focus = false): HTMLElement {
  const done = jobs.filter((c) => c.state === 'done').length
  const couldnt = jobs.filter((c) => c.state === 'booked' && c.blocked_reason).length
  const money_ = pricesHidden() ? '' : wholeText(workWords(jobs).replace(/^ · /, ''))
  // Focus mode, from the person's own heading (B1's Focus).
  let chip: HTMLElement | null = null
  if (focus) {
    chip = el('button', { type: 'button', class: 'today-focus-chip', 'aria-label': 'Focus: one stop at a time' }, lineDrawing('focus', 16), el('span', {}, 'Focus'))
    chip.addEventListener('click', enterFocus)
  }
  return el(tag, { class: 'today-group-head' },
    el('h3', { class: 'today-person' }, `${label} · ${done} of ${jobs.length}${couldnt ? ` · ${couldnt} couldn't` : ''}`),
    ...(money_ ? [el('span', { class: 'today-group-money' }, money_)] : []),
    ...(chip ? [chip] : []),
    ...(tag === 'summary' ? [el('span', { class: 'today-group-arrow', 'aria-hidden': 'true' })] : []))
}

// Which stop is open: the one tapped, else the next one to do (Liam, 13 Oct 2026: "today should open at the next one to
// do"). A job just done hands on to the next by itself (B4). The lemon job under way is always open.
let openStopId: number | null = null
let openStopDay = ''
let lastOpenKey: string | null = null
function openStopFor(stops: TodayClean[][]): number | null {
  const all = stops.flat()
  if (openStopId !== null && all.some((c) => c.id === openStopId)) return stops.find((s) => s.some((c) => c.id === openStopId))![0]!.id
  const next = stops.find(stopLeft)
  return next ? next[0]!.id : null
}

// The open stop's buttons and address come in together, never one after another, and nothing scrolls (Astra).
function revealStop(stop: HTMLElement): void {
  if (reducedMotion()) return
  for (const part of Array.from(stop.querySelectorAll<HTMLElement>(':scope > .stop-actions, :scope > .today-tools, :scope > .today-done-note, :scope > .sched-clean-main > .today-address, :scope > .sched-clean-main > .note-clip, :scope > .today-house-jobs'))) {
    part.animate?.([{ opacity: 0, transform: 'translateY(-4px)' }, { opacity: 1, transform: 'none' }], { duration: 200, easing: 'cubic-bezier(.2,.8,.2,1)' })
  }
}

// A folded stop opens where it is tapped, and the one open before folds (B1): the whole folded stop is one button, under
// its own links and buttons (the name, the price, the tick, Move to another day), so a keyboard or a screen reader opens
// it too (Astra).
function openStop(stop: HTMLElement): void {
  if (!stop.classList.contains('is-folded')) return
  for (const o of Array.from(stop.parentElement?.children ?? [])) {
    if (o instanceof HTMLElement && o.classList.contains('is-open') && !o.classList.contains('today-live')) o.classList.replace('is-open', 'is-folded')
  }
  stop.classList.replace('is-folded', 'is-open')
  openStopId = Number(stop.dataset.cleanId ?? stop.dataset.stopId)
  lastOpenKey = String(openStopId)
  revealStop(stop)
}
function stopOpener(stop: HTMLElement, who: string): HTMLButtonElement {
  const b = el('button', { type: 'button', class: 'stop-open', 'aria-label': `Open ${who}`, 'aria-expanded': 'false' }) as HTMLButtonElement
  b.addEventListener('click', () => {
    openStop(stop)
    // The button steps aside once the stop is open: the keyboard goes on to the stop's first button.
    stop.querySelector<HTMLElement>(':scope > .stop-actions button, :scope > .stop-actions a, :scope > .today-tools button, :scope > .sched-clean-main a, :scope > .sched-clean-main button')?.focus({ preventScroll: true })
  })
  return b
}

// Which days have their finished stops opened out (the green line tapped).
const doneShown = new Set<string>()
// One person's stops on the numbered line.
function stopList(visits: TodayClean[], data: TodayPayload, celebrate: Set<number>, numbered: Map<number, number>, mine: boolean): HTMLElement {
  const rerender = (): void => renderDay(data, null)
  const stops = dayStops(visits, severalJobsOn())
  const openId = mine ? openStopFor(stops) : null
  const list = el('div', { class: 'today-stops' })
  // The diary's bands, named the diary's way, only when a day has more than one (orderDay has already put them in order).
  const bands = new Set(visits.map((c) => bandOf(c)))
  let lastBand: number | null = null
  let n = 0
  let opened: HTMLElement | null = null
  // Design H (Liam, 4 Nov 2026): your finished stops fold into one green line at the top, saying what is not paid, and
  // a "Now" line follows on today. A stop finished this moment stays in place for its tick, and folds on the next draw.
  const doneBox = mine ? el('div', { class: 'today-done-box' }) : null
  const doneJobs: TodayClean[] = []
  for (const stop of stops) {
    const cl = stop[0]!
    const band = bandOf(cl)
    // Not paid stays out on the line, its payment one tap away (Sonnet): only paid or not-recorded stops fold.
    const folds = !!doneBox && stop.every((c) => c.state === 'done' && !owedOn(c)) && !stop.some((c) => celebrate.has(c.id))
    if (!folds && bands.size > 1 && band !== lastBand) { list.append(el('h4', { class: 'day-group' }, BAND_LABELS[band] ?? '')); lastBand = band }
    n++
    for (const c of stop) numbered.set(c.id, n)
    const open = openId !== null && stop.some((c) => c.id === openId)
    let card: HTMLElement
    if (!mine) {
      // Another person's: their first letter with the number, as the map's dots say it (C4: D1, D2).
      card = renderViewOnlyCard(cl, { n: `${(cl.phone_of?.trim() || '?').slice(0, 1).toUpperCase()}${n}`, open: false })
      list.append(card)
      // Someone else's house with two jobs: each job, with its own outcome, payment and note (Astra).
      for (const c of stop.slice(1)) list.append(renderViewOnlyCard(c, { n: null, open: false }))
    } else if (stop.length > 1) card = renderHouse(stop, visits, rerender, celebrate, { n, open })
    else {
      card = renderCard(cl, rerender, nextToWarn(visits, cl), false, { n, open })
      if (celebrate.has(cl.id)) card.classList.add('done-enter')
    }
    if (open) opened = card
    const into = folds ? doneBox! : list
    if (folds) doneJobs.push(...stop)
    if (card.parentNode !== list) into.append(card)
  }
  if (doneBox && doneJobs.length) {
    const key = visits[0]?.date ?? ''
    const worked = doneJobs.reduce((sum, c) => sum + (c.amount_pence ?? 0), 0)
    const taken = doneJobs.reduce((sum, c) => sum + visitReceived(c), 0)
    const fold = el('button', { type: 'button', class: 'today-done-fold', 'aria-expanded': String(doneShown.has(key)) },
      el('span', { class: 'today-done-fold-tick', 'aria-hidden': 'true' }, '✓'),
      el('span', { class: 'today-done-fold-words' }, `${doneJobs.length} done${pricesHidden() || !worked ? '' : ` · ${pounds(taken)} taken`}`),
      el('span', { class: 'today-done-fold-arrow', 'aria-hidden': 'true' }, '›'))
    doneBox.hidden = !doneShown.has(key)
    fold.addEventListener('click', () => {
      if (doneShown.has(key)) doneShown.delete(key); else doneShown.add(key)
      doneBox.hidden = !doneShown.has(key)
      fold.setAttribute('aria-expanded', String(doneShown.has(key)))
    })
    list.prepend(fold, doneBox)
  }
  // Now sits where the clock is among the stops still to do: above the first one timed later than now, so a stop still
  // to do from earlier shows above it, behind (Sonnet). With none timed later, under the last one.
  if (mine && visits[0]?.date === todayISO() && stops.some(stopLeft)) {
    const nowLine = el('div', { class: 'today-now-line', 'aria-hidden': 'true' }, el('span', {}, `Now ${shownClock(new Date())}`))
    const d = new Date()
    const nowMin = d.getHours() * 60 + d.getMinutes()
    const later = stops.find((st) => stopLeft(st) && (strictMinutes(st[0]!.time_slot) ?? -1) > nowMin)
    const at = later ? list.querySelector<HTMLElement>(`.today-clean[data-clean-id="${later[0]!.id}"], [data-stop-id="${later[0]!.id}"]`) : null
    if (at?.parentNode === list) list.insertBefore(nowLine, at)
    else if (later) list.prepend(nowLine)
    else list.append(nowLine)
  }
  // The next stop opens by itself once a job is done (B4), its buttons arriving together.
  if (mine) {
    const key = openId === null ? null : String(openId)
    if (opened && celebrate.size && key !== lastOpenKey) revealStop(opened)
    lastOpenKey = key
  }
  return list
}

// The quiet movements on a redraw (the movement set): a job's paid tag as it is saved, the red line of one that couldn't
// be done arriving with its next step, and a job put back by Undo settling in. Never on opening the page.
const lastBlocked = new Map<number, boolean>()
function markChanges(container: HTMLElement, visits: TodayClean[], celebrate: Set<number>, seenBefore: Map<number, string>): void {
  for (const cl of visits) {
    const card = container.querySelector<HTMLElement>(`.today-clean[data-clean-id="${cl.id}"]`)
    const blocked = cl.state === 'booked' && !!cl.blocked_reason
    const wasBlocked = lastBlocked.get(cl.id)
    lastBlocked.set(cl.id, blocked)
    if (!card) continue
    if (celebrate.has(cl.id)) card.querySelector('.stop-pay-tag')?.classList.add('rd-tag-in')
    if (blocked && wasBlocked === false) for (const part of Array.from(card.querySelectorAll('.blocked-line, .rearrange-btn, .sorry-btn'))) part.classList.add('rd-tag-in')
    const was = seenBefore.get(cl.id)
    if ((was === 'done' && cl.state === 'booked') || (wasBlocked === true && !blocked && cl.state === 'booked')) card.classList.add('stop-back')
  }
}

// The owner's "Everyone" view: the day grouped by whose phone each job is on, their own group (if they do jobs) first
// with its usual buttons, then everyone else's view only, folded to a heading until opened, then jobs nobody is set to.
const openGroups = new Set<string>()
function renderEveryone(container: HTMLElement, data: TodayPayload, visits: TodayClean[], cachedAt: number | null, celebrate: Set<number>, seenBefore: Map<number, string>): void {
  const groups = new Map<string, TodayClean[]>()
  for (const cl of visits) {
    const who = cl.phone_of?.trim() || ''
    if (!groups.has(who)) groups.set(who, [])
    groups.get(who)!.push(cl)
  }
  const own = ownName ? groups.get(ownName) ?? null : null
  const numbered = new Map<number, number>()
  const tally = el('p', { class: 'today-tally sr-only', 'aria-live': 'polite' },
    visits.length ? todayTallyLine(visits, !!cachedAt, false) : data.from < todayISO() ? 'No visits on this day.' : 'No visits booked.')
  if (!visits.length) {
    // The days people have finished that the owner has not looked at yet (0064), above everyone's jobs.
    container.append(tally)
    finishedDaysArea(container, 'today')
    container.append(emptyDay(data.from))
    dropTodayMap(); todayMapDone = null
    return
  }
  const top = dayTiles({ date: data.from, all: visits, own, numbered, facts: null, rerender: () => renderDay(data, null) })
  container.append(top.tiles, tally)
  finishedDaysArea(container, 'today')
  if (own) finishDayCard(container, data.from, own, () => renderDay(data, null))
  const names = [...groups.keys()].filter((n) => n).sort((a, b) => {
    if (ownName && a === ownName) return -1
    if (ownName && b === ownName) return 1
    return a.localeCompare(b)
  })
  if (groups.has('')) names.push('')
  for (const name of names) {
    const jobs = groups.get(name)!
    const mine = !!ownName && name === ownName
    // A name on jobs that nobody signs in with (an old worker's): no phone shows these, so say so rather than suggest
    // someone is out with them.
    const onNoPhone = !!name && !mine && !!data.people && !data.people.includes(name)
    if (mine) {
      const group = el('section', { class: 'today-group' }, groupHead('You', jobs, 'div', true))
      group.append(stopList(jobs, data, celebrate, numbered, true))
      container.append(group)
      continue
    }
    const key = name || '(nobody)'
    const group = el('details', { class: 'today-group today-group-view' }) as HTMLDetailsElement
    group.open = openGroups.has(key) || !own
    group.addEventListener('toggle', () => { if (group.open) openGroups.add(key); else openGroups.delete(key) })
    group.append(groupHead(name ? `${name}${onNoPhone ? ' (not on Roundly)' : ''}` : 'Nobody yet', jobs, 'summary'),
      el('p', { class: 'today-group-note' }, name && !onNoPhone ? `On ${name}'s phone · view only` : 'View only'),
      stopList(jobs, data, celebrate, new Map(), false))
    container.append(group)
  }
  deskLayout(container, data.from, visits, numbered, !own)
  markChanges(container, visits, celebrate, seenBefore)
  playPendingReveal(container)
}

// Nothing booked (E7): no tiles, one line, and the Diary one tap away for the owner, where jobs are booked.
function emptyDay(date: string): HTMLElement {
  const isToday = date === todayISO()
  return el('section', { class: 'today-empty' },
    el('span', { class: 'today-empty-mark' }, lineDrawing('calendar', 28)),
    el('strong', {}, date < todayISO() ? 'Nothing was booked' : isToday ? 'Nothing booked today' : 'Nothing booked'),
    el('p', { class: 'muted' }, isOwnerToday() ? 'Book jobs in the Diary and they show here, in order.' : 'Jobs booked for you show here, in order.'),
    ...(isOwnerToday() ? [el('a', { href: '#/schedule', class: 'btn-primary today-empty-diary' }, 'Open the Diary')] : []))
}

// The day, then the "Your jobs changed" card over it (a redraw after a save must not lose it).
function renderDay(data: TodayPayload, cachedAt: number | null): void {
  lastDrawn = { data, cachedAt, key: `${selectedDate}${whoParam()}`, gen: routeGen }
  drawDayList(data, cachedAt)
  // The first-time hint for the round tick (today-done-hint.ts), once the day's jobs are drawn.
  const dayBox = document.querySelector<HTMLElement>('#today-day')
  if (dayBox) mountTodayDoneHint(dayBox)
  drawJobsChanged()
  // What still waits to send, and what didn't send, on every drawing of the day, not only after a read (Astra).
  markWaiting()
  // A sheet taken away by this redraw (a reason saved) had a read waiting for it: it goes now.
  if (reloadWaiting && !document.querySelector('#today-day dialog.stop-sheet[open], dialog.time-sheet[open]')) { reloadWaiting = false; void load() }
}

// What each visit was when the day was last drawn, so a job finished since can be marked with its tick.
const lastStates = new Map<number, string>()

function drawDayList(data: TodayPayload, cachedAt: number | null): void {
  const container = document.querySelector<HTMLElement>('#today-day')
  if (!container) return
  clear(container)
  dayCleans = data.cleans
  if (openStopDay !== `${data.from}${whoParam()}`) { openStopId = null; lastOpenKey = null; openStopDay = `${data.from}${whoParam()}` }
  // Moves made from here whose customer has not been told yet (docs/plans/rearrange/PLAN.md).
  toTellRows(container)
  // An empty day slides in too: the animation is on the list itself, so the cards added below ride it.
  playDaySlide(container)
  // The same reading order as the owner's diary (src/day-order.ts): timed jobs first in time order, then the ones agreed
  // without a time. The van and the diary must not disagree about what order the day runs in. Several jobs on: a house's
  // jobs sit together, gutters above windows (Liam Q11).
  const visits = severalJobsOn() ? houseOrder(orderDay(data.cleans)) : orderDay(data.cleans)
  drawnVisits = visits
  // A job that was booked when this screen last drew it and is done now has just been finished: its stop plays the tick,
  // the ring settles, and the next stop opens.
  const celebrate = new Set<number>()
  const seenBefore = new Map<number, string>()
  for (const cl of visits) {
    const was = lastStates.get(cl.id)
    if (was) seenBefore.set(cl.id, was)
    if (was === 'booked' && cl.state === 'done') celebrate.add(cl.id)
    lastStates.set(cl.id, cl.state)
  }
  if (celebrate.size) openStopId = null
  // Focus mode: one of the person's own stops at a time (F1, F2).
  const own = showingMine() ? visits : ownName ? visits.filter((c) => (c.phone_of?.trim() || '') === ownName) : []
  // A link to a job that isn't one of the person's own (focusToday on someone else's) shows the day instead (Astra).
  if (focusMode && focusId !== null && !own.some((c) => c.id === focusId)) focusMode = false
  if (focusMode && own.length) {
    focusView(container, own, data, celebrate)
    markChanges(container, visits, celebrate, seenBefore)
    dayDoneMoment(container, data.from, visits, celebrate)
    playPendingReveal(container)
    dropTodayMap()
    return
  }
  focusMode = false
  focusLook(false)
  if (!showingMine()) {
    renderEveryone(container, data, visits, cachedAt, celebrate, seenBefore)
    // Day done on Everyone too (it showed on Just mine only): the whole day, everyone's jobs (27 Oct 2026).
    dayDoneMoment(container, data.from, visits, celebrate)
    return
  }
  const readonly = data.from < todayISO()
  const tally = el('p', { class: 'today-tally sr-only', 'aria-live': 'polite' },
    visits.length ? todayTallyLine(visits, !!cachedAt, pricesHidden(), true) : readonly ? 'No visits on this day.' : 'No visits booked.')
  if (!visits.length) {
    container.append(tally, emptyDay(data.from))
    dropTodayMap(); todayMapDone = null
    return
  }
  // Someone who doesn't see prices counts jobs, on today (job-count.ts).
  const counted = pricesHidden() && data.from === todayISO() && Array.isArray(data.done_days)
  const facts = counted ? jobCountFacts(todayISO(), visits.filter((c) => c.state === 'done').length, visits.length, data.done_days!) : null
  const numbered = new Map<number, number>()
  const list = stopList(visits, data, celebrate, numbered, true)
  // In someone's van (0096): the van's jobs are here to tick off, and the van's driver finishes that day.
  const mine = visits.filter((c) => !c.van_of)
  const van = visits.find((c) => c.van_of)?.van_of?.trim()
  const top = dayTiles({ date: data.from, all: visits, own: mine, numbered, facts, rerender: () => renderDay(data, null) })
  container.append(top.tiles, tally)
  if (top.count) container.append(top.count)
  if (van) container.append(el('p', { class: 'today-van-banner' }, `${data.from === todayISO() ? 'In' : `${dayLabel(data.from)} · in`} ${van}'s van. Tick the jobs off as you go; ${van} finishes the day.`))
  // A day gone (E6): ticked off and paid, never started. Said only when one is left.
  if (readonly && visits.some((c) => c.state === 'booked')) {
    container.append(el('p', { class: 'today-past-banner' }, `${data.from === addDaysISO(todayISO(), -1) ? 'Yesterday' : dayLabel(data.from)} · tick off anything you did; nothing to start. Jobs not logged yet can still be marked Done.`))
  }
  // Finish my day (0064): once every job is dealt with it waits at the top (B11); the tile opens it any time.
  finishDayCard(container, data.from, mine, () => renderDay(data, null))
  const group = el('section', { class: 'today-group' }, groupHead(isOwnerToday() && !worksAlone() ? 'You' : 'Your jobs', visits, 'div', true), list)
  // The rest of today as one route, in the day's order, from where the phone is.
  if (!readonly && data.from === todayISO()) {
    const left = visits.filter((c) => c.state === 'booked' && !c.blocked_reason && !c.finished_at)
    const href = routeHref(left)
    if (href) {
      const n = Math.min(ROUTE_MAX, left.filter((c) => placeOf(c)).length)
      group.append(el('a', { href, target: '_blank', rel: 'noopener', class: 'today-route ic ic-navigate' }, left.length > ROUTE_MAX ? `Route for the next ${n} jobs` : 'Route for the rest of today'))
    }
  }
  container.append(group)
  dayDoneMoment(container, data.from, visits, celebrate)
  deskLayout(container, data.from, visits, numbered, false)
  markChanges(container, visits, celebrate, seenBefore)
  playPendingReveal(container)
}

// The last job of the day done: "Day done", once a day on this phone, with Messages' drawn tick and sparks, a buzz and
// the day's count and money; it folds away after a few seconds (docs/ANIMATION-STANDARD.md: finishing a pile earns a
// bigger moment, once; Liam, 22 Oct 2026: "Any brand animation or satisfying animations we can add").
function dayDoneMoment(container: HTMLElement, day: string, visits: TodayClean[], celebrate: Set<number>): void {
  if (!visits.length) return
  const place = (box: HTMLElement): void => { const at = container.querySelector('.today-tally') ?? container.querySelector('.today-tiles'); if (at) at.after(box); else container.prepend(box) }
  if (celebrate.size) showDayDone(place, day, visits, pricesHidden()); else keepDayDone(place, day, visits)
}

// ---- Today on a computer (design C, 24 Oct 2026: boards C1 to C7) ------------------------------------------------------
// The tiles across the top (with who is out, for the owner watching), the stops on the left, and the day on a map on the
// right with the list's numbers (another person's stops D1, D2; a job under way dark; a stop just done ripples once).
// An owner who does no jobs sees each person's day side by side (C6). The map is drawn once and kept while the same
// stops are on show, as the Diary's Day keeps its own; a phone never loads it.
const DESK = '(min-width: 1100px)'
function deskWide(): boolean {
  return typeof matchMedia === 'function' && matchMedia(DESK).matches
}
let lastDrawn: { data: TodayPayload; cachedAt: number | null; key: string; gen: number } | null = null
let todayMap: { key: string; map: import('../day-map-panel').DeskDayMap } | null = null
let todayMapDone: { day: string; ids: Set<number> } | null = null
let todayMapAsk = 0
function dropTodayMap(): void { todayMapAsk++; todayMap?.map.remove(); todayMap = null }
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('hashchange', () => { if (!window.location.hash.startsWith('#/today')) { dropTodayMap(); todayMapDone = null } })
  // A window made wider or narrower past a computer's width draws the day again in the other shape.
  // Only the day and view on show, never an earlier one still on its way (Astra); and not while something is being
  // answered or typed on a stop: the next redraw takes the new shape.
  if (typeof matchMedia === 'function') matchMedia(DESK).addEventListener?.('change', () => {
    const day = document.querySelector<HTMLElement>('#today-day')
    if (!lastDrawn || !day || lastDrawn.gen !== routeGen || day.dataset.day !== lastDrawn.key || lastDrawn.key !== `${selectedDate}${whoParam()}`) return
    if (day.querySelector('dialog.stop-sheet[open], .blocked-chooser, .late-chooser, .time-chooser, .change-times, .rearrange-panel, .sorry-chooser, .access-note-editor, .voice-recording, .voice-starting, .photo-pick:not([hidden]), details.live-more[open]')) return
    renderDay(lastDrawn.data, lastDrawn.cachedAt)
  })
}

function deskLayout(container: HTMLElement, date: string, visits: TodayClean[], numbered: Map<number, number>, side: boolean): void {
  if (!deskWide() || !visits.length) { dropTodayMap(); return }
  const tiles = container.querySelector<HTMLElement>(':scope > .today-tiles')
  if (!tiles) return
  const main = el('div', { class: `today-desk-main${side ? ' is-side' : ''}` })
  for (let n = tiles.nextSibling; n;) { const next = n.nextSibling; main.append(n); n = next }
  const stops = dayStops(visits, severalJobsOn())
  const rows = mapStops(stops, numbered)
  const left = stops.filter(stopLeft).length
  // "Yours 1 to 3 · Dave D1, D2" on Everyone (C4); "4 stops left · numbers match the list" on one person's day (C1).
  const byPerson = new Map<string, string[]>()
  stops.forEach((st, i) => { const label = rows[i]?.label; if (label) { const who = st[0]!.phone_of?.trim() || 'Not set to anyone'; byPerson.set(who, [...(byPerson.get(who) ?? []), label]) } })
  const mineNos = rows.filter((r) => r.n !== null && r.n !== undefined).map((r) => r.n as number)
  const sub = byPerson.size
    ? [mineNos.length ? `Yours ${Math.min(...mineNos)} to ${Math.max(...mineNos)}` : '', ...[...byPerson].map(([who, labels]) => `${who} ${labels.join(', ')}`)].filter(Boolean).join(' · ')
    : `${left ? `${left} ${left === 1 ? 'stop' : 'stops'} left` : 'Every stop dealt with'} · numbers match the list`
  const bigger = el('button', { type: 'button', class: 'btn-secondary today-map-bigger' }, 'Bigger map')
  bigger.addEventListener('click', () => { void import('../day-map-panel').then((panel) => panel.openDayMap(rows, dayLabel(date))) })
  const host = el('div', { class: 'today-map-host' })
  const aside = el('aside', { class: 'today-desk-map', 'aria-label': 'The day on a map' },
    el('div', { class: 'today-map-head' }, el('div', {}, el('h3', {}, 'The day on a map'), el('p', { class: 'today-map-sub' }, sub)), bigger), host)
  container.append(el('div', { class: 'today-desk' }, main, aside))
  // The stops marked done since the map last showed this day ripple once (the movement set); nothing on opening it.
  const doneNow = new Set(rows.filter((r) => r.state === 'done').map((r) => r.id))
  const before = todayMap && todayMapDone?.day === date ? todayMapDone.ids : null
  todayMapDone = { day: date, ids: doneNow }
  const just = new Set(before ? [...doneNow].filter((id) => !before.has(id)) : [])
  const key = JSON.stringify([date, rows.map((r) => [r.id, r.n ?? null, r.label ?? null, r.state, r.blocked_reason ?? null, !!r.live, r.customer_lat ?? null, r.customer_lng ?? null, r.customer_name ?? null, r.customer_address ?? null, r.customer_postcode ?? null])])
  if (todayMap?.key === key) {
    host.append(todayMap.map.holder)
    const map = todayMap.map
    requestAnimationFrame(() => map.resize())
    return
  }
  dropTodayMap()
  const ask = todayMapAsk
  host.classList.add('is-loading')
  void import('../day-map-panel').then((panel) => {
    if (ask !== todayMapAsk || !host.isConnected) return
    todayMap = { key, map: panel.drawDeskDayMap(rows, just) }
    host.classList.remove('is-loading')
    host.append(todayMap.map.holder)
    todayMap.map.resize()
  }).catch(() => {
    host.classList.remove('is-loading')
    if (host.isConnected) host.replaceChildren(el('p', { class: 'muted' }, 'The map could not load. Check your signal and try again.'))
  })
}

// ---- Focus mode (F1, F2; Liam, 24 Oct 2026: "a toggle ... so it's like a focus mode", with Previous and Next buttons as
// well as a swipe) ------------------------------------------------------------------------------------------------------
// One of the person's own stops at a time, big, for wet hands and gloves: its number, name and address, one line of notes,
// Start job and Done, then Directions, Contact, Voice note and ⋯; the job under way lemon with Slide to finish. Previous
// and Next (or a swipe) move a stop; a job done hands on to the next one still to do, sliding in from the right (the
// movement set's slide-in, 220ms). Moving between stops never counts anything done. Back to list returns to the day.
let focusMode = false
let focusStopId: number | null = null
let focusDir: 1 | -1 | 0 = 0
let focusStops: number[] = []
function focusLook(on: boolean): void {
  document.querySelector('#screen')?.classList.toggle('today-focus-on', on)
}
// Something being answered, typed or recorded on the day: Focus doesn't move, enter or leave until it is done (Astra).
const FOCUS_HOLD = '.blocked-chooser, .late-chooser, .time-chooser:not(.time-chooser-asked), .change-times, .rearrange-panel, .sorry-chooser, .access-note-editor, .voice-recording, .voice-starting, .photo-pick:not([hidden]), dialog.stop-sheet[open]'
// The leaving question in the slide's place counts once its time has been changed (Astra): marked on the first change,
// since a tap on Next has already taken the keyboard away from it.
if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
  for (const kind of ['input', 'change']) {
    document.addEventListener(kind, (e) => { (e.target as Element | null)?.closest?.('.time-chooser-asked')?.setAttribute('data-edited', '1') }, true)
  }
}
function focusHeld(): boolean {
  if (!document.querySelector(`#today-day :is(${FOCUS_HOLD}, .time-chooser-asked[data-edited])`)) return false
  toast('Finish or close what is open first.')
  return true
}
function enterFocus(): void {
  if (focusHeld()) return
  focusMode = true
  focusStopId = openStopId
  focusDir = 0
  if (lastDrawn) renderDay(lastDrawn.data, lastDrawn.cachedAt)
  window.scrollTo({ top: 0, behavior: 'auto' })
  document.querySelector<HTMLElement>('#today-day .focus-head h2')?.focus({ preventScroll: true })
}
function leaveFocus(): void {
  if (focusHeld()) return
  focusMode = false
  openStopId = focusStopId
  focusStopId = null
  focusLook(false)
  if (lastDrawn) renderDay(lastDrawn.data, lastDrawn.cachedAt)
  window.scrollTo({ top: 0, behavior: 'auto' })
  // The keyboard goes back to where Focus was opened.
  document.querySelector<HTMLElement>('#today-day .today-focus-chip')?.focus({ preventScroll: true })
}
function moveFocus(step: 1 | -1): void {
  const at = focusStops.indexOf(focusStopId ?? -1)
  const to = at + step
  if (at < 0 || to < 0 || to >= focusStops.length || !lastDrawn || focusHeld()) return
  focusStopId = focusStops[to]!
  focusDir = step
  renderDay(lastDrawn.data, lastDrawn.cachedAt)
  // The keyboard and a screen reader land on the new stop's heading, which says where they are.
  document.querySelector<HTMLElement>('#today-day .focus-head h2')?.focus({ preventScroll: true })
}
function focusView(container: HTMLElement, visits: TodayClean[], data: TodayPayload, celebrate: Set<number>): void {
  focusLook(true)
  const rerender = (): void => renderDay(data, null)
  const stops = dayStops(visits, severalJobsOn())
  focusStops = stops.map((s) => s[0]!.id)
  // A link to a job (the lock screen card, What's next) opens Focus on its stop, once: moving on stays moved (Astra).
  const asked = focusId
  if (asked !== null && stops.some((s) => s.some((c) => c.id === asked))) { focusStopId = stops.find((s) => s.some((c) => c.id === asked))![0]!.id; focusId = null }
  let at = focusStopId === null ? -1 : stops.findIndex((s) => s.some((c) => c.id === focusStopId))
  if (at < 0 || (celebrate.size && !stopLeft(stops[at]!) && stops[at]!.some((c) => celebrate.has(c.id)))) {
    const next = stops.findIndex(stopLeft)
    if (at >= 0 && next >= 0 && next !== at) focusDir = 1
    at = next >= 0 ? next : Math.max(0, at)
  }
  const stop = stops[at]!
  focusStopId = stop[0]!.id
  const back = el('button', { type: 'button', class: 'focus-back' }, lineDrawing('list', 16), el('span', {}, 'Back to list'))
  back.addEventListener('click', leaveFocus)
  // Not today: the day stays in sight, and on a day gone what can still be done (Astra).
  const day = data.from === todayISO() ? null
    : data.from < todayISO() ? `${dayLabel(data.from)} · a day gone: tick off what you did; nothing to start`
    : `${dayLabel(data.from)} · a day to come: for reading and getting there`
  container.append(el('div', { class: 'focus-head' }, el('h2', { tabindex: '-1', 'aria-live': 'polite' }, `Focus · stop ${at + 1} of ${stops.length}`), back),
    ...(day ? [el('p', { class: 'today-past-banner focus-day' }, day)] : []),
    el('div', { class: 'focus-progress', 'aria-hidden': 'true' }, ...stops.map((s, i) => el('span', { class: [i === at ? 'is-here' : '', s.every((c) => c.state === 'done') ? 'is-done' : ''].filter(Boolean).join(' ') }))))
  const card = stop.length > 1
    ? renderHouse(stop, visits, rerender, celebrate, { n: at + 1, open: true, focus: true })
    : renderCard(stop[0]!, rerender, nextToWarn(visits, stop[0]!), false, { n: at + 1, open: true, focus: true })
  if (celebrate.has(stop[0]!.id)) card.classList.add('done-enter')
  container.append(el('div', { class: 'today-stops is-focus-list' }, card))
  const prev = el('button', { type: 'button', class: 'btn-secondary focus-prev' }, lineDrawing('chevronLeft', 16), el('span', {}, 'Previous')) as HTMLButtonElement
  prev.disabled = at === 0
  prev.addEventListener('click', () => moveFocus(-1))
  const after = stops[at + 1]
  const next = el('button', { type: 'button', class: 'btn-secondary focus-next' },
    el('span', {}, after ? `Next: ${at + 2} ${after[0]!.customer_name}` : 'The last stop'), ...(after ? [lineDrawing('chevronRight', 16)] : [])) as HTMLButtonElement
  next.disabled = !after
  next.addEventListener('click', () => moveFocus(1))
  container.append(el('div', { class: 'focus-nav' }, prev, next))
  // Straight in from the side it lies on, never on opening Focus (Astra: no tilt; Back reverses it).
  if (focusDir && !reducedMotion()) card.animate?.([{ transform: `translateX(${focusDir * 40}px)`, opacity: 0 }, { transform: 'none', opacity: 1 }], { duration: 220, easing: 'cubic-bezier(.2,.8,.2,1)' })
  focusDir = 0
}
